import { computed, inject, signal } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  withProps,
  patchState
} from '@ngrx/signals';
import {
  BillFile,
  BillGroup,
  OnboardingContext,
  OnboardingLevel,
  BillStatus,
  ParsedBillData,
  AddressInput
} from '../../../models';
import {
  BillImportMatchRequest,
  BillImportMatchResponse,
  BillImportRequest,
  BillImport,
  LegalEntityImport,
  ServiceAddressImport,
  MeterImport,
  LegalEntityMatchInput,
  ServiceAddressMatchInput,
  MeterMatchInput,
  BillMatchInput,
  MatchSuggestion,
  DuplicateWarning,
  DuplicateAction,
  DuplicateResolution,
  OverlapType
} from '../../../models/bill-import.models';
import { firstValueFrom } from 'rxjs';
import { gasUnitIdFromName } from '../../../services/bill-persistence.service';
import { CommodityType } from '../../../../zentility/_shared/_zen-legacy-common/_models/commodity';
import { BillImportService } from '../../../services/bill-import.service';
import { BillService } from '../../../services/bill.service';
import { ContractsV4Service } from '../../../../zentility/_shared/_services/v4/contracts-v4.service';
import { CustomerPlanFinderEntry } from '../../../../zentility/_shared/_model/contract-v4.model';
import { mergePairedBillData, billCommodity } from '../../../utils/bill-utils';
import { formatBillDate, formatBillPeriod } from '../../../utils/date-format.util';
import { buildAddressGroupingKey } from '../../../utils/address-normalization.util';
import { buildLenIdentityKey } from '../../../utils/entity-identity.util';
import {
  BillAiFlowStep,
  BillAiFlowStepConfig,
  SelectOption,
  BillNavEntry,
  MeterEditData,
  SaEditData,
  NewCustomerContact,
  NewCustomerDetails,
  NewCustomerPreview,
  BillAiFlowStats,
  CrossLenDuplicateGroup,
  DuplicateLenGroup,
  DuplicateDecision,
  ExistingBillRef,
  FastTrackAnalysis
} from './bill-ai-flow.models';
import { BillAiFlowState, UndoSnapshot, initialBillAiFlowState } from './bill-ai-flow.state';
import { AUTO_ACCEPT_SCORE, MIN_DISPLAY_SCORE, SUPPLIER_AUTO_ACCEPT_SCORE, shouldAutoAcceptMatch } from '../../../utils/match-score.util';
import { withPersistence } from './bill-ai-flow-persistence.feature';
import { validateLenStep, LenAddress } from '../bill-ai-flow-len-step/bill-ai-flow-len-step.validation';
import { validateSaStep } from '../bill-ai-flow-sa-step/bill-ai-flow-sa-step.validation';
import { validateMeterStep } from '../bill-ai-flow-meter-step/bill-ai-flow-meter-step.validation';
import { validateBillReviewStep, resolveMeterContext, isSupplyDisabled, isDistributionDisabled, isDualHalf } from '../bill-ai-flow-bill-review-step/bill-ai-flow-bill-review-step.validation';
import { effectiveDuplicateAction, isResolvableDuplicate, normalizeRestoredResolutions } from './duplicate-resolution.util';
import {
  buildAmbiguousMatchFragment,
  buildFieldPauseReason,
  formatUnappliedMatchWarning,
  hasDisplayableOptions,
  joinPauseReason,
  LEN_KIND,
  METER_KIND,
  PauseBlocker,
  SA_KIND,
  UnappliedMatchWarning,
} from './fast-track-messages';
import { PortfolioCustomerCreationRequest, CustomerPrimaryContact } from '../../../../zentility/_shared/_model/customer-v4.model';
import { ValidPatterns } from '../../../../zentility/_shared/_zen-legacy-common/_models/common/email-pattern';

const EMAIL_REGEXP = new RegExp(ValidPatterns.EMAIL_PATTERN);
import { CustomerAndServiceAddressType } from '../../../../zentility/_shared/_zen-legacy-common/_enums/entity-types.enum';
import { LenV4Service } from '../../../../zentility/_shared/_services/v4/len-v4.service';
import { ServiceAddressService } from '../../../../zentility/_shared/_services/v4/service-address.service';
import { LenV4FormModel } from '../../../../zentility/_modules/portfolio/_model/portfolio-lens.model';
import { ServiceAddressAddUpdateDTO, ServiceAddressDetailsModel } from '../../../../zentility/_shared/_model/service-address.model';
import { DISTRIBUTION_BUCKET_KEYS, SUPPLY_BUCKET_KEYS, lineItemsForImport } from '../../../services/bill-persistence.service';

// =============================================================================
// Helper Functions (outside the store)
// =============================================================================

/**
 * Creates a deep-copy snapshot of the current match response and entity selections.
 * Returns null if serialization fails (e.g. circular references).
 */
function createUndoSnapshot(
  response: BillImportMatchResponse,
  entitySelections: Record<string, number | string | null>,
  label: string,
  request: BillImportMatchRequest
): UndoSnapshot | null {
  try {
    return {
      matchResponse: JSON.parse(JSON.stringify(response)),
      entitySelections: { ...entitySelections },
      matchRequest: JSON.parse(JSON.stringify(request))
    };
  } catch (e) {
    console.error(`[BillAiFlowStore] Failed to create undo snapshot for ${label} — operation aborted to prevent data loss:`, e);
    return null;
  }
}

/**
 * Pushes a snapshot onto an undo stack (immutably). Returns the original stack if snapshot is null.
 */
function pushUndoSnapshot(stack: UndoSnapshot[], snapshot: UndoSnapshot | null): UndoSnapshot[] {
  return snapshot ? [...stack, snapshot] : stack;
}

function getStepsForLevel(level: OnboardingLevel): BillAiFlowStepConfig[] {
  const allSteps: BillAiFlowStepConfig[] = [
    { key: 'intro', label: 'Start', icon: 'waving_hand' },
    { key: 'customer', label: 'Customer', icon: 'language' },
    { key: 'len', label: 'Legal Entities', icon: 'account_balance' },
    { key: 'sa', label: 'Addresses', icon: 'home_work' },
    { key: 'meter', label: 'Meters', icon: 'speed' },
    { key: 'bill-review', label: 'Bills', icon: 'receipt_long' },
    { key: 'summary', label: 'Import', icon: 'cloud_upload' }
  ];

  switch (level) {
    case 'PORTFOLIO':
      return allSteps.filter(s => s.key !== 'customer');
    case 'CUSTOMER':
      return allSteps.filter(s => s.key !== 'customer');
    case 'LEN':
      return allSteps.filter(s => s.key !== 'customer' && s.key !== 'len');
    case 'SERVICE_ADDRESS':
      return allSteps.filter(s => !['customer', 'len', 'sa'].includes(s.key));
    case 'METER':
      return allSteps.filter(s => !['customer', 'len', 'sa', 'meter'].includes(s.key));
    default:
      return allSteps;
  }
}

/**
 * Build a BillImportMatchRequest from processed bills.
 * Also returns billData and billFileIdMapping for the store.
 *
 * Exported so the SA-dedup behavior (two bills whose street1 differs only
 * by unit designator collapse into one ServiceAddressMatchInput) can be
 * unit-tested without spinning up the whole signal store.
 */
export function buildMatchRequest(
  processedBills: BillFile[],
  customerId: number | null,
  pairs: BillGroup[] = []
): {
  request: BillImportMatchRequest;
  billData: Record<string, ParsedBillData>;
  billFileIdMapping: Record<string, string>;
  billFilesMap: Map<string, BillFile>;
} {
  const billData: Record<string, ParsedBillData> = {};
  const billFileIdMapping: Record<string, string> = {};
  const billFilesMap = new Map<string, BillFile>();

  // A confirmed pair is ONE bill in the match request: the utility entry
  // carries the canonical merged record (supply section from the supplier bill,
  // provider from whichever half resolved a supplier, supplier bill first,
  // everything else from the utility bill), and the supplier bill contributes
  // nothing but its PDF. Downstream — validation, forms,
  // supplier matching, energy plans, import — sees a single combined bill.
  const presentIds = new Set(processedBills.map(b => b.id));
  const pairedSupplierIds = new Set<string>();
  const mergedDataByUtilityId = new Map<string, ParsedBillData>();
  for (const pair of pairs) {
    if (pair.utilityBill?.data && pair.supplierBill?.data &&
        presentIds.has(pair.utilityBill.id) && presentIds.has(pair.supplierBill.id)) {
      pairedSupplierIds.add(pair.supplierBill.id);
      mergedDataByUtilityId.set(pair.utilityBill.id, mergePairedBillData(pair.utilityBill.data, pair.supplierBill.data));
    }
  }

  const entityMap = new Map<string, {
    name: string;
    billingAddress?: AddressInput;
    addresses: Map<string, {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      meters: Map<string, {
        uan1: string;
        uan2?: string;
        utilityName?: string;
        rateClass?: string;
        commodityType: CommodityType;
        bills: BillFile[];
      }>;
    }>;
  }>();

  for (const bill of processedBills) {
    // A paired supplier bill is absorbed into its utility bill's merged
    // record; only its PDF is kept (for the Supplier tab in the wizard).
    if (pairedSupplierIds.has(bill.id)) {
      billFilesMap.set(bill.id, bill);
      continue;
    }
    const merged = mergedDataByUtilityId.get(bill.id);
    const data = merged ?? bill.data!;
    const entityName = (data.legalEntity || 'Unknown Entity').trim();
    const street1 = (data.serviceAddressStreet1 || '').trim();
    const street2 = (data.serviceAddressStreet2 || '').trim();
    const city = (data.serviceAddressCity || '').trim();
    const state = (data.serviceAddressState || '').trim();
    const zip = (data.serviceAddressZip || '').trim();
    const uan1 = (data.utilityAccountNumber || '').trim();
    const uan2 = (data.utilityAccountNumber2 || '').trim();
    const utilityName = (data.utilityName || '').trim();
    const addressKey = buildAddressGroupingKey(street1, city, state);
    // Meters are per-commodity: a dual-fuel PDF's electric and gas bills share a
    // UAN but must group into separate (elec/gas) meters.
    const meterKey = `${uan1 || `meter-${bill.id}`}|${billCommodity(bill)}`;

    if (!entityMap.has(entityName)) {
      entityMap.set(entityName, { name: entityName, addresses: new Map() });
    }
    const entity = entityMap.get(entityName)!;
    if (!entity.billingAddress && (data.billingAddressStreet1 || data.billingAddressCity || data.billingAddressZip)) {
      entity.billingAddress = {
        street1: data.billingAddressStreet1 || '',
        street2: data.billingAddressStreet2 || '',
        city: data.billingAddressCity || '',
        state: data.billingAddressState || '',
        zip: data.billingAddressZip || ''
      };
    }

    // First bill to collide on the normalized key wins for display. We
    // deliberately don't stitch street1/street2 across bills: if the key
    // says they're the same SA, use one set of fields and keep the user
    // able to edit on the SA step. Merging leaks redundant unit fragments
    // ("…*2fl , 2fl") when different bills put the unit in different slots.
    if (!entity.addresses.has(addressKey)) {
      entity.addresses.set(addressKey, {
        street1,
        street2: street2 || undefined,
        city,
        state,
        zip,
        meters: new Map()
      });
    }
    const address = entity.addresses.get(addressKey)!;

    if (!address.meters.has(meterKey)) {
      address.meters.set(meterKey, {
        uan1, uan2: uan2 || undefined, utilityName, rateClass: data.rateClass || undefined,
        commodityType: billCommodity(bill), bills: []
      });
    }
    const meterGroup = address.meters.get(meterKey)!;
    // Any bill on the meter can supply the tariff text the first one lacked.
    if (!meterGroup.rateClass && data.rateClass) meterGroup.rateClass = data.rateClass;
    // Paired utility bills join their meter carrying the merged record.
    meterGroup.bills.push(merged ? { ...bill, data: merged } : bill);
  }

  const legalEntities: LegalEntityMatchInput[] = [];
  let groupCounter = 1;

  entityMap.forEach((entity) => {
    const lenGroupId = `len-${groupCounter++}`;
    const serviceAddresses: ServiceAddressMatchInput[] = [];

    entity.addresses.forEach((addr) => {
      const saGroupId = `sa-${groupCounter++}`;
      const meters: MeterMatchInput[] = [];

      addr.meters.forEach((meter) => {
        const meterGroupId = `meter-${groupCounter++}`;
        const bills: BillMatchInput[] = meter.bills.map(billFile => {
          const billGroupId = `bill-${groupCounter++}`;
          billData[billGroupId] = billFile.data!;
          billFileIdMapping[billGroupId] = billFile.id;
          billFilesMap.set(billFile.id, billFile);
          return {
            groupId: billGroupId,
            billStartDate: billFile.data!.billingPeriodStart || '',
            billEndDate: billFile.data!.billingPeriodEnd || '',
            totalUsage: billFile.data!.usageAmount,
            providerName: billFile.data!.providerName,
            // Raw parsed amounts — the API uses which sides carry values to
            // classify an overlap as pairable (dual-bill half) vs duplicate.
            supplyCharges: billFile.data!.supplyCharges ?? undefined,
            supplyTotal: billFile.data!.supplyTotalCost ?? undefined,
            distributionCharges: billFile.data!.distributionCharges ?? undefined,
            distributionTotal: billFile.data!.distributionTotalCost ?? undefined
          };
        });

        // Per-commodity meter groups — the meter key already includes commodity.
        const commodityType: CommodityType = meter.commodityType;

        meters.push({
          groupId: meterGroupId,
          utilityAccountNumber1: meter.uan1,
          utilityAccountNumber2: meter.uan2,
          utilityName: meter.utilityName,
          rateClassText: meter.rateClass,
          commodityType,
          bills
        });
      });

      serviceAddresses.push({
        groupId: saGroupId,
        street1: addr.street1,
        street2: addr.street2,
        city: addr.city,
        state: addr.state,
        zip: addr.zip,
        meters
      });
    });

    legalEntities.push({
      groupId: lenGroupId,
      name: entity.name,
      billingAddress: entity.billingAddress,
      serviceAddresses
    });
  });

  return {
    request: { customerId, legalEntities },
    billData,
    billFileIdMapping,
    billFilesMap
  };
}

/**
 * Inject committed existing-entity ids from the wizard's selections into a
 * freshly built match request. A committed id short-circuits matching for
 * that row and narrows the candidate pool for its children on the backend.
 * Exported for unit testing.
 */
export function applySelectionsToRequest(
  request: BillImportMatchRequest,
  selections: Record<string, number | string | null>
): void {
  const committed = (groupId: string | undefined): number | string | null => {
    if (!groupId) return null;
    const value = selections[groupId];
    return isNewSelection(value) ? null : value!;
  };

  for (const len of request.legalEntities) {
    const lenId = committed(len.groupId);
    if (lenId !== null) len.id = String(lenId);
    for (const sa of len.serviceAddresses) {
      const saId = committed(sa.groupId);
      if (saId !== null && !isNaN(Number(saId))) sa.id = Number(saId);
      for (const meter of sa.meters) {
        const meterId = committed(meter.groupId);
        if (meterId !== null && !isNaN(Number(meterId))) meter.id = Number(meterId);
      }
    }
  }
}

/**
 * Strips committed entity ids from a reused request so it reflects only the
 * CURRENT selections — a stale id would survive a pick changing to
 * "create new" otherwise. applySelectionsToRequest / applyContextIdsToRequest
 * re-add the ones that still apply.
 */
function clearCommittedIds(request: BillImportMatchRequest): void {
  for (const len of request.legalEntities) {
    delete len.id;
    for (const sa of len.serviceAddresses) {
      delete sa.id;
      for (const meter of sa.meters) {
        delete meter.id;
      }
    }
  }
}

/**
 * Commit the entry-level context ids onto the request so the FIRST /match is
 * already parent-scoped. Without this the initial request goes out with all
 * ids null and the backend matches customer-wide — a meter UAN hit under a
 * different SA would auto-accept and silently link outside the hierarchy the
 * user is importing into. Mirrors applyContextSelections (intro step), which
 * force-selects the same ids into entitySelections after the response.
 * Exported for unit testing.
 */
export function applyContextIdsToRequest(
  request: BillImportMatchRequest,
  context: OnboardingContext
): void {
  const atOrBelow = (...levels: OnboardingLevel[]) => levels.includes(context.level);

  for (const len of request.legalEntities) {
    if (context.lenId && atOrBelow('LEN', 'SERVICE_ADDRESS', 'METER')) {
      len.id = context.lenId;
    }
    for (const sa of len.serviceAddresses) {
      if (context.serviceAddressId && atOrBelow('SERVICE_ADDRESS', 'METER')
          && !isNaN(Number(context.serviceAddressId))) {
        sa.id = Number(context.serviceAddressId);
      }
      for (const meter of sa.meters) {
        if (context.meterId && atOrBelow('METER') && !isNaN(Number(context.meterId))) {
          meter.id = Number(context.meterId);
        }
      }
    }
  }
}

/**
 * Merge freshly initialized selections with the user's existing picks.
 * Explicit picks (an id or 'new_record') survive a re-match — the backend
 * echoes committed ids as resolvedId with a null match, so score-based
 * initialization alone would wipe them. Untouched rows (null) adopt the new
 * response's auto-accept, if any. Exported for unit testing.
 */
export function mergeSelections(
  existing: Record<string, number | string | null>,
  initialized: Record<string, number | string | null>
): Record<string, number | string | null> {
  const merged: Record<string, number | string | null> = { ...initialized };
  for (const key of Object.keys(initialized)) {
    const prior = existing[key];
    if (prior !== null && prior !== undefined) {
      merged[key] = prior;
    }
  }
  return merged;
}

/**
 * Collapse import entries that resolve to the same existing record — but only
 * at the tiers the entry context pins (applyContextIdsToRequest): LEN at LEN
 * entry, LEN+SA at SERVICE_ADDRESS entry, all three at METER entry. Pinned-tier
 * same-id siblings are the wizard's own bookkeeping — not a user match mistake —
 * and must merge rather than fail the import. Below the pinned tier, same-id
 * rows are the user's own matches and pass through untouched so per-row edits
 * (e.g. a meter's rate class) keep reaching the backend. The merge mutates its
 * input in place and keeps the first entry's scalar fields, concatenating only
 * children. Exported for unit testing.
 */
export function mergeSameIdLegalEntities(legalEntities: LegalEntityImport[], level: OnboardingLevel): LegalEntityImport[] {
  const mergeById = <T extends { id?: string | number | null }>(
    items: T[],
    absorb: (into: T, from: T) => void
  ): T[] => {
    const byId = new Map<string, T>();
    const out: T[] = [];
    for (const item of items) {
      const existing = item.id != null ? byId.get(String(item.id)) : undefined;
      if (existing) {
        absorb(existing, item);
        continue;
      }
      if (item.id != null) byId.set(String(item.id), item);
      out.push(item);
    }
    return out;
  };

  const mergeSas = level === 'SERVICE_ADDRESS' || level === 'METER';
  const mergeMeters = level === 'METER';

  const merged = mergeById(legalEntities, (into, from) => into.serviceAddresses.push(...from.serviceAddresses));
  if (!mergeSas) return merged;
  for (const len of merged) {
    len.serviceAddresses = mergeById(len.serviceAddresses, (into, from) => into.meters.push(...from.meters));
    if (!mergeMeters) continue;
    for (const sa of len.serviceAddresses) {
      sa.meters = mergeById(sa.meters, (into, from) => into.bills.push(...from.bills));
    }
  }
  return merged;
}

/**
 * Group IDs of bills whose parent meter will be created NEW — its own
 * selection is "create new", or an ancestor customer/LEN/SA is new (the
 * cascade forces the meter to new). Duplicate warnings from the match
 * response compare against an existing meter's bills; when the meter is
 * created new the imported bill lands on that new meter, so those warnings
 * don't apply. Exported for unit testing.
 */
/** Whether a selection value means "create new" (null/undefined, or the legacy 'new_record'). */
export function isNewSelection(sel: number | string | null | undefined): boolean {
  return sel === null || sel === undefined || sel === 'new_record';
}

export function computeNewMeterBillGroupIds(
  matchResponse: BillImportMatchResponse | null,
  selections: Record<string, number | string | null>,
  isNewCustomer: boolean
): Set<string> {
  const ids = new Set<string>();
  if (!matchResponse) return ids;
  const isNew = (gid: string) => isNewSelection(selections[gid]);
  for (const len of matchResponse.legalEntities) {
    const lenNew = isNewCustomer || isNew(len.groupId);
    for (const sa of len.serviceAddresses) {
      const saNew = lenNew || isNew(sa.groupId);
      for (const meter of sa.meters) {
        if (saNew || isNew(meter.groupId)) {
          for (const bill of meter.bills) {
            ids.add(bill.groupId);
          }
        }
      }
    }
  }
  return ids;
}

// ─── SA duplicate-detection reason constants ────────────────────────────────
/** Two import SAs were matched to the same existing portfolio SA (will auto-merge on import). */
export const REASON_RESOLVED = 'Matched to same existing service address';
/** Same address appears under different LENs — suspicious, user should verify. */
export const REASON_ADDRESS = 'Same address appears under different legal entities';
/** Same address appears multiple times under the same LEN — will auto-merge on import. */
export const REASON_SAME_LEN_ADDRESS = 'Same address appears multiple times under the same legal entity';

/**
 * Detect SA duplicate groups across the import. Pure function so it can be
 * unit-tested without spinning up the signal store. Callers downstream
 * partition the result into same-LEN (auto-merge on import) vs cross-LEN
 * (suspicious — please verify) groups based on `reason` and `lenGroupIds`.
 *
 * Exported alongside `buildMatchRequest` specifically so duplicate-detection
 * behaviour can be unit-tested without TestBed setup.
 */
export function computeSaDuplicateGroups(
  matchResponse: BillImportMatchResponse | null,
  matchRequest: BillImportMatchRequest | null,
  selections: Record<string, number | string | null>,
  saEdits: Record<string, SaEditData>,
): CrossLenDuplicateGroup[] {
  if (!matchResponse) return [];

  // Pre-build a groupId → request-SA index so we can resolve fallback values
  // in O(1) per response SA instead of nested-looping the matchRequest tree.
  const reqSaMap = new Map<string, ServiceAddressMatchInput>();
  if (matchRequest) {
    for (const reqLen of matchRequest.legalEntities) {
      for (const reqSa of reqLen.serviceAddresses) {
        reqSaMap.set(reqSa.groupId, reqSa);
      }
    }
  }

  const allSas: Array<{
    saGroupId: string;
    lenGroupId: string;
    resolvedId: number | null;
    addressKey: string;
  }> = [];

  for (const len of matchResponse.legalEntities) {
    for (const sa of len.serviceAddresses) {
      const sel = selections[sa.groupId];
      let resolvedId: number | null = null;
      if (sel && sel !== 'new_record') {
        const parsed = Number(sel);
        resolvedId = Number.isNaN(parsed) ? null : parsed;
      }

      // Per-field precedence: user edit (even empty) wins over the request,
      // request wins over absent. Using ?? (not ||) so an explicit clear in
      // one field doesn't silently get re-populated from the extracted value.
      // buildAddressGroupingKey applies the same unit/floor stripping the
      // match request uses, so detection matches what the backend collapses.
      const edit = saEdits[sa.groupId];
      const reqSa = reqSaMap.get(sa.groupId);
      const addressKey = buildAddressGroupingKey(
        edit?.street1 ?? reqSa?.street1,
        edit?.city ?? reqSa?.city,
        edit?.state ?? reqSa?.state,
      );

      allSas.push({
        saGroupId: sa.groupId,
        lenGroupId: len.groupId,
        resolvedId,
        addressKey,
      });
    }
  }

  const groups: CrossLenDuplicateGroup[] = [];
  const caughtSaIds = new Set<string>();

  // Resolved-id buckets — same existing portfolio SA matched (any LEN combo)
  const byResolved = new Map<number, typeof allSas>();
  for (const sa of allSas) {
    if (sa.resolvedId !== null) {
      if (!byResolved.has(sa.resolvedId)) byResolved.set(sa.resolvedId, []);
      byResolved.get(sa.resolvedId)!.push(sa);
    }
  }
  for (const [, group] of byResolved) {
    if (group.length < 2) continue;
    const uniqueLens = [...new Set(group.map(s => s.lenGroupId))];
    groups.push({
      saGroupIds: group.map(s => s.saGroupId),
      lenGroupIds: uniqueLens,
      reason: REASON_RESOLVED,
    });
    group.forEach(s => caughtSaIds.add(s.saGroupId));
  }

  // Address-key buckets — distinguish same-LEN vs cross-LEN by lenGroupIds count
  const byAddress = new Map<string, typeof allSas>();
  for (const sa of allSas) {
    if (caughtSaIds.has(sa.saGroupId)) continue;
    if (!sa.addressKey || sa.addressKey === '||') continue;
    if (!byAddress.has(sa.addressKey)) byAddress.set(sa.addressKey, []);
    byAddress.get(sa.addressKey)!.push(sa);
  }
  for (const [, group] of byAddress) {
    if (group.length < 2) continue;
    const uniqueLens = [...new Set(group.map(s => s.lenGroupId))];
    const reason = uniqueLens.length === 1 ? REASON_SAME_LEN_ADDRESS : REASON_ADDRESS;
    groups.push({
      saGroupIds: group.map(s => s.saGroupId),
      lenGroupIds: uniqueLens,
      reason,
    });
  }

  return groups;
}

/**
 * Detect identical-LEN groups in the match request. Pure function so it can
 * be unit-tested without spinning up the signal store.
 *
 * Two LENs are "identical" when their normalized name matches (lowercased +
 * trimmed). The returned `identical` flag distinguishes:
 *   - true  → name + billing-address fingerprint also match → safe auto-merge
 *   - false → name matches but billing addresses differ → destructive merge,
 *             one billing address gets discarded by the backend
 *
 * No level gate is needed: at LEN/SA/Meter level extracted-name variants can
 * form several LEN groups, but they all force-link to context.lenId and merge
 * at import time (mergeSameIdLegalEntities), so a hit here only matters where
 * the Legal Entities step exists.
 *
 * Skips LENs with blank names (no merge collision risk).
 */
export function computeIdenticalLenGroups(
  matchRequest: BillImportMatchRequest | null,
  entityNameOverrides: Record<string, string>,
): DuplicateLenGroup[] {
  if (!matchRequest) return [];

  const lens = matchRequest.legalEntities.map(len => ({
    groupId: len.groupId,
    key: buildLenIdentityKey(
      entityNameOverrides[len.groupId] ?? len.name ?? '',
      len.billingAddress,
    ),
  }));

  const groups: DuplicateLenGroup[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < lens.length; i++) {
    if (seen.has(lens[i].groupId)) continue;
    if (!lens[i].key.name) continue; // blank names don't merge

    const cluster = [lens[i]];
    for (let j = i + 1; j < lens.length; j++) {
      if (lens[i].key.name === lens[j].key.name) {
        cluster.push(lens[j]);
      }
    }
    if (cluster.length < 2) continue;

    cluster.forEach(c => seen.add(c.groupId));
    const allSameAddress = cluster.every(c => c.key.addressKey === cluster[0].key.addressKey);
    groups.push({
      lenGroupIds: cluster.map(c => c.groupId),
      identical: allSameAddress,
    });
  }
  return groups;
}

/**
 * Collapses a LEN list for a merge: the target absorbs the removed LENs'
 * service addresses and keeps its own fields, notably its billing address.
 * Shared by the matchResponse and matchRequest sides so they can't drift. Pure.
 */
export function collapseLenList<T extends { groupId?: string; serviceAddresses: unknown[] }>(
  lens: T[], targetGroupId: string, removedGroupIds: string[],
): T[] {
  const removed = new Set(removedGroupIds);
  const isRemoved = (l: T) => l.groupId != null && removed.has(l.groupId);
  const absorbed = lens.filter(isRemoved).flatMap(l => l.serviceAddresses);
  return lens
    .filter(l => !isRemoved(l))
    .map(l => l.groupId === targetGroupId
      ? { ...l, serviceAddresses: [...l.serviceAddresses, ...absorbed] } as T
      : l);
}

/**
 * Collapses a service-address list for a merge: the target SA absorbs the
 * removed SAs' meters (across all LENs) and keeps its own address fields.
 * Shared by the matchResponse and matchRequest sides so they can't drift. Pure.
 */
export function collapseSaList<T extends { serviceAddresses: any[] }>(
  lens: T[], targetGroupId: string, removedGroupIds: string[],
): T[] {
  const removed = new Set(removedGroupIds);
  const isRemoved = (sa: any) => sa.groupId != null && removed.has(sa.groupId);
  const absorbed = lens.flatMap(l => l.serviceAddresses.filter(isRemoved)).flatMap(sa => sa.meters);
  return lens.map(l => ({
    ...l,
    serviceAddresses: l.serviceAddresses
      .filter(sa => !isRemoved(sa))
      .map(sa => sa.groupId === targetGroupId
        ? { ...sa, meters: [...sa.meters, ...absorbed] }
        : sa),
  }));
}

/**
 * Collapses a meter list for a merge: the target meter absorbs the removed
 * meters' bills (across all SAs) and keeps its own fields, notably its UANs.
 * Shared by the matchResponse and matchRequest sides so they can't drift. Pure.
 */
export function collapseMeterList<T extends { serviceAddresses: any[] }>(
  lens: T[], targetGroupId: string, removedGroupIds: string[],
): T[] {
  const removed = new Set(removedGroupIds);
  const isRemoved = (m: any) => m.groupId != null && removed.has(m.groupId);
  const absorbed = lens
    .flatMap(l => l.serviceAddresses)
    .flatMap(sa => sa.meters.filter(isRemoved))
    .flatMap((m: any) => m.bills);
  return lens.map(l => ({
    ...l,
    serviceAddresses: l.serviceAddresses.map(sa => ({
      ...sa,
      meters: sa.meters
        .filter((m: any) => !isRemoved(m))
        .map((m: any) => m.groupId === targetGroupId
          ? { ...m, bills: [...m.bills, ...absorbed] }
          : m),
    })),
  }));
}

/**
 * Drops SAs (and their meters) from every LEN. Shared by the matchResponse
 * and matchRequest sides of an SA delete so they can't drift. Pure.
 */
export function dropSasFromList<T extends { serviceAddresses: any[] }>(
  lens: T[], deleteIds: string[],
): T[] {
  const removed = new Set(deleteIds);
  return lens.map(l => ({
    ...l,
    serviceAddresses: l.serviceAddresses.filter(sa => !removed.has(sa.groupId)),
  }));
}

/**
 * Drops meters from every SA. Shared by the matchResponse and matchRequest
 * sides of a meter delete so they can't drift. Pure.
 */
export function dropMetersFromList<T extends { serviceAddresses: any[] }>(
  lens: T[], deleteIds: string[],
): T[] {
  const removed = new Set(deleteIds);
  return lens.map(l => ({
    ...l,
    serviceAddresses: l.serviceAddresses.map(sa => ({
      ...sa,
      meters: sa.meters.filter((m: any) => !removed.has(m.groupId)),
    })),
  }));
}

/**
 * Returns a new matchRequest with the address fields of one service address
 * (matched by groupId) overwritten. Pure — does not mutate the input.
 */
function patchSaAddressInRequest(
  request: BillImportMatchRequest,
  saGroupId: string,
  address: { street1: string; street2: string; city: string; state: string; zip: string },
): BillImportMatchRequest {
  return {
    ...request,
    legalEntities: request.legalEntities.map(len => ({
      ...len,
      serviceAddresses: len.serviceAddresses.map(sa =>
        sa.groupId === saGroupId
          ? { ...sa, street1: address.street1, street2: address.street2, city: address.city, state: address.state, zip: address.zip }
          : sa
      ),
    })),
  };
}

/**
 * Initialize entity selections from match response.
 *
 * A row's initial selection is set to the match id when the match is confident
 * (score >= AUTO_ACCEPT_SCORE), otherwise null (= "create new"). Using the
 * shared `shouldAutoAcceptMatch` keeps this in lock-step with the fast-track
 * analyzer — see utils/match-score.util.ts for the rationale.
 */
function initializeSelections(response: BillImportMatchResponse): Record<string, number | string | null> {
  const selections: Record<string, number | string | null> = {};
  for (const len of response.legalEntities) {
    selections[len.groupId] = shouldAutoAcceptMatch(len.match) ? len.match!.id : null;
    for (const sa of len.serviceAddresses) {
      selections[sa.groupId] = shouldAutoAcceptMatch(sa.match) ? sa.match!.id : null;
      for (const meter of sa.meters) {
        selections[meter.groupId] = shouldAutoAcceptMatch(meter.match) ? meter.match!.id : null;
      }
    }
  }
  return selections;
}

/**
 * Rows with an exact in-scope duplicate are locked to Link — override any
 * "create new" selection (including a preserved user pick) with the match id.
 * LEN/SA exactness comes from the backend's exactMatch flag; meter matches
 * are UAN-exact by construction at score 1.0. Exported for unit testing.
 */
export function forceExactSelections(
  selections: Record<string, number | string | null>,
  response: BillImportMatchResponse
): void {
  const force = (groupId: string, match: MatchSuggestion | null | undefined) => {
    if (match && isNewSelection(selections[groupId])) {
      selections[groupId] = match.id;
    }
  };
  for (const len of response.legalEntities) {
    if (len.exactMatch === true) force(len.groupId, len.match);
    for (const sa of len.serviceAddresses) {
      if (sa.exactMatch === true && !sa.crossParentMatch) force(sa.groupId, sa.match);
      for (const meter of sa.meters) {
        if (meter.match && meter.match.score >= 1 && !meter.crossParentMatch) {
          force(meter.groupId, meter.match);
        }
      }
    }
  }
}

/**
 * The backend short-circuits committed rows and echoes them with match: null.
 * That would blank the linked row's label in the step dropdowns after any
 * re-match, so carry the previous response's match data forward for rows the
 * user has already committed. Exported for unit testing.
 */
export function preserveCommittedMatchData(
  response: BillImportMatchResponse,
  prev: BillImportMatchResponse,
  selections: Record<string, number | string | null>
): void {
  type MatchRow = {
    groupId: string;
    match: MatchSuggestion | null;
    alternatives: MatchSuggestion[];
    exactMatch?: boolean | null;
  };

  const prevByGroupId = new Map<string, MatchRow>();
  for (const len of prev.legalEntities) {
    prevByGroupId.set(len.groupId, len);
    for (const sa of len.serviceAddresses) {
      prevByGroupId.set(sa.groupId, sa);
      for (const meter of sa.meters) prevByGroupId.set(meter.groupId, meter);
    }
  }

  const isCommitted = (groupId: string) => !isNewSelection(selections[groupId]);
  const restore = (row: MatchRow) => {
    if (row.match || !isCommitted(row.groupId)) return;
    const old = prevByGroupId.get(row.groupId);
    if (!old?.match) return;
    row.match = old.match;
    if (!row.alternatives?.length && old.alternatives?.length) {
      row.alternatives = old.alternatives;
    }
    if (row.exactMatch == null && old.exactMatch != null) {
      row.exactMatch = old.exactMatch;
    }
  };

  for (const len of response.legalEntities) {
    restore(len);
    for (const sa of len.serviceAddresses) {
      restore(sa);
      for (const meter of sa.meters) restore(meter);
    }
  }
}


// =============================================================================
// The Store
// =============================================================================

export const BillAiFlowStore = signalStore(
  withState<BillAiFlowState>(initialBillAiFlowState),
  withPersistence(),
  withProps(() => ({
    _billImportService: inject(BillImportService),
    _billService: inject(BillService),
    _contractsV4Service: inject(ContractsV4Service),
    _lenService: inject(LenV4Service),
    _saService: inject(ServiceAddressService),
    _billFiles: new Map<string, BillFile>(),
    // Reactive so computeds/components that read it via getContext() track it as
    // a dependency. Plain-property reads would only re-evaluate when some other
    // signal happened to change in the same cycle (the original cause of the
    // DEV-14897 "stuck in a loop" bug).
    _context: signal<OnboardingContext | null>(null),
  })),

  // ---------------------------------------------------------------------------
  // Computed Signals
  // ---------------------------------------------------------------------------
  withComputed((store) => ({
    /** Current bill group ID from navigation + index */
    currentBillGroupId: computed(() => {
      const idx = store.currentBillIndex();
      const nav = store.billNavigation();
      if (idx >= 0 && idx < nav.length) {
        return nav[idx].billGroupId;
      }
      return null;
    }),

    /** Current bill's ParsedBillData */
    currentBillData: computed(() => {
      const idx = store.currentBillIndex();
      const nav = store.billNavigation();
      if (idx >= 0 && idx < nav.length) {
        return store.billData()[nav[idx].billGroupId] ?? null;
      }
      return null;
    }),

    /** Whether the customer match has a high-confidence result */
    hasGoodCustomerMatch: computed(() => {
      const match = store.customerMatch();
      return !!(match?.match && match.match.score >= 0.8);
    }),

    /** Customer select options for the template (filters out matches below 35%) */
    customerOptions: computed<SelectOption[]>(() => {
      const match = store.customerMatch();
      if (!match) return [{ value: null, label: 'Create New Customer', isCreateNew: true }];

      const options: SelectOption[] = [];

      if (match.match && match.match.score >= MIN_DISPLAY_SCORE) {
        options.push({
          value: match.match.id,
          label: match.match.label,
          score: match.match.score,
          matchReasons: match.match.matchReasons
        });
      }

      for (const alt of match.alternatives || []) {
        if (alt.score < MIN_DISPLAY_SCORE) continue;
        if (!match.match || alt.id !== match.match.id) {
          options.push({
            value: alt.id,
            label: alt.label,
            score: alt.score,
            matchReasons: alt.matchReasons
          });
        }
      }

      options.push({ value: null, label: 'Create New Customer', isCreateNew: true });
      return options;
    }),

    /** Cascade rule: if customer is new, all children are new */
    isNewCustomer: computed(() =>
      store.createdCustomerUpfront() ||
      (store.selectedCustomerId() === null && store.completedSteps().includes('customer'))
    ),

    /**
     * Group IDs of rows blocked by a cross-entity duplicate (crossParentMatch):
     * the entity already exists under a DIFFERENT parent on this customer.
     * Resolved by migrating the import to the customer level or editing the row.
     */
    crossEntityBlockedGroupIds: computed<Set<string>>(() => {
      const resp = store.matchResponse();
      const ids = new Set<string>();
      if (!resp) return ids;
      for (const len of resp.legalEntities) {
        for (const sa of len.serviceAddresses) {
          if (sa.crossParentMatch) ids.add(sa.groupId);
          for (const meter of sa.meters) {
            if (meter.crossParentMatch) ids.add(meter.groupId);
          }
        }
      }
      return ids;
    }),

  })),

  // Bills whose parent meter will be created new — see computeNewMeterBillGroupIds.
  withComputed((store) => ({
    newMeterBillGroupIds: computed(() =>
      computeNewMeterBillGroupIds(store.matchResponse(), store.entitySelections(), store.isNewCustomer())
    ),
  })),

  withComputed((store) => ({
    /** Bills with duplicates (duplicatesMap entries, minus bills on new meters) */
    billsWithDuplicates: computed(() => {
      const dupsMap = store.duplicatesMap();
      const suppressed = store.newMeterBillGroupIds();
      const duplicates: { billGroupId: string; duplicate: DuplicateWarning }[] = [];
      for (const [billGroupId, dup] of Object.entries(dupsMap)) {
        if (suppressed.has(billGroupId)) continue;
        duplicates.push({ billGroupId, duplicate: dup });
      }
      return duplicates;
    }),

    /** Stats broken down by hierarchy level */
    stats: computed<BillAiFlowStats>(() => {
      const response = store.matchResponse();
      if (!response) {
        return {
          legalEntities: { total: 0, new: 0, existing: 0 },
          serviceAddresses: { total: 0, new: 0, existing: 0 },
          meters: { total: 0, new: 0, existing: 0 },
          bills: { total: 0, duplicates: 0 }
        };
      }

      const sel = store.entitySelections();
      const suppressedDups = store.newMeterBillGroupIds();
      const le = { total: 0, new: 0, existing: 0 };
      const sa = { total: 0, new: 0, existing: 0 };
      const m = { total: 0, new: 0, existing: 0 };
      const b = { total: 0, duplicates: 0 };

      for (const len of response.legalEntities) {
        le.total++;
        if (sel[len.groupId] === null || sel[len.groupId] === undefined) le.new++;
        else le.existing++;

        for (const addr of len.serviceAddresses) {
          sa.total++;
          if (sel[addr.groupId] === null || sel[addr.groupId] === undefined) sa.new++;
          else sa.existing++;

          for (const meter of addr.meters) {
            m.total++;
            if (sel[meter.groupId] === null || sel[meter.groupId] === undefined) m.new++;
            else m.existing++;

            b.total += meter.bills.length;
            b.duplicates += meter.bills.filter(bill => bill.duplicate && !suppressedDups.has(bill.groupId)).length;
          }
        }
      }

      return { legalEntities: le, serviceAddresses: sa, meters: m, bills: b };
    }),
  })),

  // After the first withComputed, add blocking/unresolved duplicates that depend on billsWithDuplicates
  withComputed((store) => ({
    /** Blocking duplicates — PARTIAL, CONTAINS, CONTAINED_BY — cannot proceed.
     *  Pairable overlaps (missing half of an existing dual bill) are not blocking:
     *  they merge into the existing bill at import. */
    blockingDuplicates: computed(() => {
      const rejected = store.rejectedPairSuggestions();
      return store.billsWithDuplicates().filter(d =>
        (!d.duplicate.pairable || rejected[d.billGroupId]) &&
        (d.duplicate.overlapType === 'PARTIAL' ||
        d.duplicate.overlapType === 'CONTAINS' ||
        d.duplicate.overlapType === 'CONTAINED_BY')
      );
    }),

    /** Unresolved EXACT/NEAR_EXACT duplicates */
    unresolvedDuplicates: computed(() => {
      const resolutions = store.duplicateResolutions();
      const rejected = store.rejectedPairSuggestions();
      return store.billsWithDuplicates().filter(d =>
        (!d.duplicate.pairable || rejected[d.billGroupId]) &&
        (d.duplicate.overlapType === 'EXACT' || d.duplicate.overlapType === 'NEAR_EXACT') &&
        !resolutions[d.billGroupId]
      );
    }),

    /** Every exact duplicate the user can skip or replace, with the action in effect
     *  (no choice yet counts as Skip). */
    duplicateDecisions: computed<DuplicateDecision[]>(() => {
      const resolutions = store.duplicateResolutions();
      const rejected = store.rejectedPairSuggestions();
      return store.billsWithDuplicates()
        .filter(d => isResolvableDuplicate(d.duplicate, !!rejected[d.billGroupId]))
        .map(d => ({
          billGroupId: d.billGroupId,
          duplicate: d.duplicate,
          action: effectiveDuplicateAction(d.duplicate, resolutions[d.billGroupId]),
        }));
    }),

    /** Selected customer option detail */
    selectedCustomerOption: computed<SelectOption | null>(() => {
      const id = store.selectedCustomerId();
      return store.customerOptions().find(o => o.value === id) ?? null;
    }),

    /** Dynamic message from Jen Zen based on current step */
    jenZenMessage: computed(() => {
      const step = store.currentStep();
      const isNew = store.isNewCustomer();

      switch (step) {
        case 'intro':
          if (store.isMatching()) {
            return "Searching for matching records in your portfolio...";
          }
          const match = store.customerMatch()?.match;
          if (match && match.score >= 0.8 && store.selectedCustomerId() === null) {
            return 'I found a potential match! Select a customer to link to, or create a new one.';
          }
          return "Let's get started. I've analyzed your bills and I'm ready to help you organize them.";
        case 'customer':
          return "Select a customer to link these bills to. If you don't see the right one, you can create a new customer record.";
        case 'len':
          if (isNew) {
            return 'All legal entities from your bills will be created as new.';
          }
          return 'Link extracted legal entities to existing records in our system, or create them as new.';
        case 'sa':
          if (isNew) {
            return 'All service addresses will be created as new under their respective legal entities.';
          }
          return 'Match service addresses to existing records or create new ones.';
        case 'meter':
          if (isNew) {
            return 'All meters will be created as new under their respective service addresses.';
          }
          return 'Match meters to existing records or create new ones. Meters are matched by utility account numbers and commodity type.';
        case 'bill-review':
          if (store.fastTrackMode() === 'fast') {
            return "Here's a quick overview of your bills. Review them and continue to import.";
          }
          return "Let's review the details for each bill. I've extracted the usage and cost information for you.";
        case 'summary':
          if (store.importSuccess()) {
            return "Excellent! Your bills have been successfully imported into the portfolio.";
          }
          return "Ready to import. Click 'Start Import' to proceed.";
        default:
          return null;
      }
    }),

    /**
     * When Express Import landed on the current step (step has a
     * skipBlockReasons entry and is not in fastTrackSkippedSteps), returns
     * the reason string so the shell can render a warning banner explaining
     * why Fast Track paused here. Null otherwise.
     */
    jenZenPauseReason: computed<string | null>(() => {
      if (store.fastTrackMode() !== 'fast') return null;
      const step = store.currentStep();
      if (step === 'intro' || step === 'summary') return null;
      const analysis = store.fastTrackAnalysis();
      const reason = analysis?.skipBlockReasons?.[step];
      if (!reason) return null;
      if (store.fastTrackSkippedSteps().includes(step)) return null;
      return reason;
    }),
    /** Whether a LEN merge can be undone */
    canUndoLenMerge: computed(() => store.lenMergeUndoStack().length > 0),
    /** Whether a LEN delete can be undone */
    canUndoLenDelete: computed(() => store.lenDeleteUndoStack().length > 0),
    /** Whether a SA merge can be undone */
    canUndoSaMerge: computed(() => store.saMergeUndoStack().length > 0),
    /** Whether a SA delete can be undone */
    canUndoSaDelete: computed(() => store.saDeleteUndoStack().length > 0),
    /** Whether a meter merge can be undone */
    canUndoMeterMerge: computed(() => store.meterMergeUndoStack().length > 0),
    /** Whether a meter delete can be undone */
    canUndoMeterDelete: computed(() => store.meterDeleteUndoStack().length > 0),

    /** Cross-LEN duplicate SAs — same address under different LENs (suspicious, user should verify) */
    crossLenDuplicateSas: computed<CrossLenDuplicateGroup[]>(() => {
      const groups = computeSaDuplicateGroups(
        store.matchResponse(),
        store.matchRequest(),
        store.entitySelections(),
        store.saEdits(),
      );
      return groups.filter(g => g.lenGroupIds.length > 1 && g.reason === REASON_ADDRESS);
    }),

    /** Same-LEN duplicate SAs — same address within one LEN, or both resolved to the same existing SA (will auto-merge on import) */
    sameLenDuplicateSas: computed<CrossLenDuplicateGroup[]>(() => {
      const groups = computeSaDuplicateGroups(
        store.matchResponse(),
        store.matchRequest(),
        store.entitySelections(),
        store.saEdits(),
      );
      return groups.filter(g => g.lenGroupIds.length === 1 || g.reason === REASON_RESOLVED);
    }),

    /** Identical-LEN groups where name + billing address match → safe auto-merge.
     *  No level gate (matches SA dedupe pattern): at LEN/SA/Meter levels every
     *  group force-links to the entry LEN and merges at import time. */
    identicalLensSafe: computed<DuplicateLenGroup[]>(() =>
      computeIdenticalLenGroups(store.matchRequest(), store.entityNameOverrides())
        .filter(g => g.identical)
    ),

    /** Identical-LEN groups where name matches but billing address differs → destructive merge.
     *  No level gate (matches SA dedupe pattern). */
    identicalLensDestructive: computed<DuplicateLenGroup[]>(() =>
      computeIdenticalLenGroups(store.matchRequest(), store.entityNameOverrides())
        .filter(g => !g.identical)
    ),

  })),

  // Duplicate bills the user decided on — skipped ones never reach the import request.
  withComputed((store) => ({
    skippedBillGroupIds: computed(() =>
      new Set(store.duplicateDecisions().filter(d => d.action === 'SKIP').map(d => d.billGroupId))),
    replacedBillGroupIds: computed(() =>
      new Set(store.duplicateDecisions().filter(d => d.action === 'REPLACE').map(d => d.billGroupId))),
  })),

  // Derived from crossLenDuplicateSas / sameLenDuplicateSas (needs separate withComputed since it references siblings)
  withComputed((store) => ({
    /** Whether there are any cross-LEN duplicate SAs */
    hasCrossLenDuplicateSas: computed(() => store.crossLenDuplicateSas().length > 0),
    /** Whether there are any same-LEN duplicate SAs (will auto-merge on import) */
    hasSameLenDuplicateSas: computed(() => store.sameLenDuplicateSas().length > 0),
    /** Whether there is a destructive LEN duplicate that blocks import */
    hasBlockingLenDuplicate: computed(() => store.identicalLensDestructive().length > 0),

    /**
     * True when the bill's extracted SA state differs from the parent SA state
     * at SERVICE_ADDRESS / METER level entries. State can't be changed on an
     * existing SA without breaking utility/meter linkage, so this gates the
     * SA-step Continue button. The user must abandon the import or fix the
     * bill's state to match.
     */
    hasSaStateMismatch: computed(() => {
      const ctx = store._context();
      if (!ctx?.serviceAddress) return false;
      if (ctx.level !== 'SERVICE_ADDRESS' && ctx.level !== 'METER') return false;
      const request = store.matchRequest();
      if (!request) return false;
      const saEdits = store.saEdits();
      const norm = (s: string | undefined | null): string => (s || '').trim().toLowerCase();
      const parentState = norm(ctx.serviceAddress.state);
      if (!parentState) return false;
      for (const len of request.legalEntities) {
        for (const sa of len.serviceAddresses) {
          const edit = saEdits[sa.groupId];
          const extractedState = norm(edit?.state ?? sa.state ?? '');
          if (extractedState && extractedState !== parentState) return true;
        }
      }
      return false;
    }),

    /** Fast track validation warnings for summary page */
    fastTrackWarnings: computed<string[]>(() => {
      if (store.fastTrackMode() !== 'fast') return [];

      const warnings: string[] = [];
      const response = store.matchResponse();
      const billData = store.billData();

      if (!response) return warnings;

      // Check for missing utility on any meter
      for (const len of response.legalEntities) {
        for (const sa of len.serviceAddresses) {
          for (const meter of sa.meters) {
            if (!meter.utility?.utilityId) {
              const uan = meter.bills[0]?.groupId
                ? (billData[meter.bills[0].groupId]?.utilityAccountNumber || meter.groupId)
                : meter.groupId;
              warnings.push(`Meter ${uan} is missing a utility assignment.`);
            }
          }
        }
      }

      // Check for low-confidence supplier auto-applications
      for (const len of response.legalEntities) {
        for (const sa of len.serviceAddresses) {
          for (const meter of sa.meters) {
            for (const bill of meter.bills) {
              if (bill.supplier?.supplierId != null && bill.supplier.confidence < SUPPLIER_AUTO_ACCEPT_SCORE) {
                const data = billData[bill.groupId];
                const period = data ? formatBillPeriod(data.billingPeriodStart, data.billingPeriodEnd, bill.groupId) : bill.groupId;
                warnings.push(`Supplier for bill (${period}) was auto-assigned with ${Math.round(bill.supplier.confidence * 100)}% confidence.`);
              }
            }
          }
        }
      }

      // Deferred energy plans
      if (store.fastTrackEnergyPlanChoice() === 'later') {
        const analysis = store.fastTrackAnalysis();
        const count = analysis?.needsEnergyPlanBills || 0;
        warnings.push(`Draft energy plans were created for ${count} bill(s). Finalize them from contract management to enable rate checks.`);
      }

      // Unresolved supplier
      for (const len of response.legalEntities) {
        for (const sa of len.serviceAddresses) {
          for (const meter of sa.meters) {
            for (const bill of meter.bills) {
              const data = billData[bill.groupId];
              if (data?.providerName && !data.supplierId && bill.supplier?.supplierId != null) {
                warnings.push(`Bill with provider "${data.providerName}" could not be resolved to an existing supplier record.`);
              }
            }
          }
        }
      }

      // Possible matches we didn't auto-apply (score between MIN_DISPLAY_SCORE
      // and AUTO_ACCEPT_SCORE). The match API is defensive — it checks for
      // accidental duplicates at every hierarchy level — so these signals
      // fire even when the cascade rule means the row WILL be created new.
      // See FAST-TRACK.md § Summary Warnings.
      const analysis = store.fastTrackAnalysis();
      if (analysis?.unappliedMatchWarnings) {
        for (const w of analysis.unappliedMatchWarnings) {
          warnings.push(formatUnappliedMatchWarning(w));
        }
      }

      return warnings;
    }),
  })),

  // ---------------------------------------------------------------------------
  // Methods
  // ---------------------------------------------------------------------------
  withMethods((store) => ({
    // =========================================================================
    // Navigation
    // =========================================================================

    goToStep(step: BillAiFlowStep): void {
      patchState(store, { currentStep: step, error: null, previewBillFileId: null });
      (store as any).saveState();
    },

    completeStep(step: BillAiFlowStep): void {
      const current = store.completedSteps();
      if (!current.includes(step)) {
        patchState(store, { completedSteps: [...current, step] });
        (store as any).saveState();
      }
    },

    goBack(): void {
      const stepList = store.steps();
      const currentIdx = stepList.findIndex(s => s.key === store.currentStep());
      if (currentIdx > 0) {
        let prevIdx = currentIdx - 1;
        // In fast track mode, skip over already-completed steps when going back
        if (store.fastTrackMode() === 'fast') {
          const skipped = store.fastTrackSkippedSteps();
          while (prevIdx > 0 && skipped.includes(stepList[prevIdx].key)) {
            prevIdx--;
          }
        }
        patchState(store, { currentStep: stepList[prevIdx].key, error: null, previewBillFileId: null });
        (store as any).saveState();
      }
    },

    goForward(): void {
      const stepList = store.steps();
      const currentIdx = stepList.findIndex(s => s.key === store.currentStep());
      if (currentIdx < stepList.length - 1) {
        const currentStep = store.currentStep();
        const completed = store.completedSteps();

        // In fast track mode, skip over already-completed steps
        let nextIdx = currentIdx + 1;
        if (store.fastTrackMode() === 'fast') {
          while (nextIdx < stepList.length - 1 && completed.includes(stepList[nextIdx].key)) {
            nextIdx++;
          }
        }

        const updates: Partial<BillAiFlowState> = {
          currentStep: stepList[nextIdx].key,
          previewBillFileId: null
        };
        if (!completed.includes(currentStep)) {
          updates.completedSteps = [...completed, currentStep];
        }
        patchState(store, updates);
        (store as any).saveState();
      }
    },

    getNextStep(): BillAiFlowStep | null {
      const stepList = store.steps();
      const currentIdx = stepList.findIndex(s => s.key === store.currentStep());
      return currentIdx < stepList.length - 1 ? stepList[currentIdx + 1].key : null;
    },

    getPreviousStep(): BillAiFlowStep | null {
      const stepList = store.steps();
      const currentIdx = stepList.findIndex(s => s.key === store.currentStep());
      return currentIdx > 0 ? stepList[currentIdx - 1].key : null;
    },

    resetSelections(): void {
      patchState(store, {
        selectedCustomerId: null,
        selectedCustomerLabel: '',
        treatAsNew: false,
        createdCustomerUpfront: false,
        entitySelections: {},
        lenMergeSelection: [],
        saMergeSelection: [],
        meterMergeSelection: [],
        meterEdits: {},
        saEdits: {},
        completedSteps: []
      });
      (store as any).saveState();
    },

    // =========================================================================
    // State Setters (for direct state updates from components)
    // =========================================================================

    setSelectedCustomerId(id: number | string | null): void {
      patchState(store, { selectedCustomerId: id });
      (store as any).saveState();
    },

    setSelectedCustomerLabel(label: string): void {
      patchState(store, { selectedCustomerLabel: label });
      (store as any).saveState();
    },

    setTreatAsNew(value: boolean): void {
      patchState(store, { treatAsNew: value });
      (store as any).saveState();
    },

    setCreatedCustomerUpfront(value: boolean): void {
      patchState(store, { createdCustomerUpfront: value });
      (store as any).saveState();
    },

    setError(error: string | null): void {
      patchState(store, { error });
    },

    setIsLoading(value: boolean): void {
      patchState(store, { isLoading: value });
    },

    setIsMatching(value: boolean): void {
      patchState(store, { isMatching: value });
    },

    setImportSuccess(value: boolean): void {
      patchState(store, { importSuccess: value });
    },

    requestClose(): void {
      patchState(store, { closeRequested: true });
    },

    /**
     * Check whether deleting the current selection would remove all remaining
     * items at the given hierarchy level (across the entire response tree).
     */
    wouldDeleteAll(level: 'len' | 'sa' | 'meter'): boolean {
      const response = store.matchResponse();
      if (!response) return true;

      if (level === 'len') {
        const deleteIds = new Set(store.lenMergeSelection());
        return response.legalEntities.every(l => deleteIds.has(l.groupId));
      }

      if (level === 'sa') {
        const deleteIds = new Set(store.saMergeSelection());
        return response.legalEntities.every(l =>
          l.serviceAddresses.every(sa => deleteIds.has(sa.groupId))
        );
      }

      if (level === 'meter') {
        const deleteIds = new Set(store.meterMergeSelection());
        return response.legalEntities.every(l =>
          l.serviceAddresses.every(sa =>
            sa.meters.every(m => deleteIds.has(m.groupId))
          )
        );
      }

      return false;
    },

    // =========================================================================
    // Initialization
    // =========================================================================

    initFromContext(context: OnboardingContext, bills: BillFile[]): void {
      store._context.set(context);

      // Store all BillFile objects in the private map
      for (const bill of bills) {
        store._billFiles.set(bill.id, bill);
      }

      const steps = getStepsForLevel(context.level);
      patchState(store, { steps });

      // Set storage key for persistence
      (store as any).setStorageKey(context.storageKey);

      // Try to restore bill AI flow state from storage
      let saved = (store as any).loadSavedState() as Partial<BillAiFlowState> | null;

      // Staleness check: verify saved state's bills overlap with current bills.
      // If the user uploaded new files after cancelling, the saved wizard state
      // references old bill IDs and must be discarded to avoid jumping to a stale step.
      if (saved && saved.matchRequest && saved.currentStep) {
        const savedBillFileIds = new Set(Object.values(saved.billFileIdMapping ?? {}));
        const currentBillIds = new Set(bills.map(b => b.id));
        if (savedBillFileIds.size > 0 && currentBillIds.size > 0) {
          const hasOverlap = [...savedBillFileIds].some(id => currentBillIds.has(id));
          if (!hasOverlap) {
            console.warn('[BillAiFlowStore] Saved state references different bills than current set. Discarding stale state.');
            (store as any).clearPersistedState();
            saved = null;
          }
        }
      }

      // Bills deleted from the dashboard since the session was saved must not
      // come back on restore — prune them from the saved state.
      // Skipped when bills is empty: an empty set here means the caller has no
      // file context, not that everything was deleted.
      let prunedOnRestore = false;
      if (saved && saved.matchRequest && bills.length > 0) {
        try {
          const pruned = pruneDeletedBillsFromSavedState(saved, new Set(bills.map(b => b.id)));
          if (!pruned) {
            console.warn('[BillAiFlowStore] All bills in saved state were deleted. Discarding stale state.');
            (store as any).clearPersistedState();
            saved = null;
          } else {
            prunedOnRestore = pruned !== saved;
            saved = pruned;
          }
        } catch (error) {
          // Same recovery as a failed restore: a malformed saved tree must not
          // wedge the wizard on every entry.
          console.error('Error pruning deleted bills from saved state:', error);
          (store as any).clearPersistedState();
          saved = null;
        }
      }

      if (saved && saved.matchRequest && saved.currentStep) {
        try {
          // Restore state from persistence
          patchState(store, {
            currentStep: saved.currentStep as BillAiFlowStep,
            completedSteps: saved.completedSteps || [],
            matchRequest: saved.matchRequest!,
            matchResponse: saved.matchResponse ?? null,
            customerMatch: saved.customerMatch ?? null,
            selectedCustomerId: saved.selectedCustomerId ?? null,
            selectedCustomerLabel: saved.selectedCustomerLabel ?? '',
            treatAsNew: saved.treatAsNew ?? false,
            createdCustomerUpfront: saved.createdCustomerUpfront ?? false,
            newCustomerContact: saved.newCustomerContact ?? initialBillAiFlowState.newCustomerContact,
            newCustomerDetails: saved.newCustomerDetails ?? initialBillAiFlowState.newCustomerDetails,
            entitySelections: saved.entitySelections ?? {},
            selectionsDirtySinceMatch: saved.selectionsDirtySinceMatch ?? false,
            meterEdits: saved.meterEdits ?? {},
            saEdits: saved.saEdits ?? {},
            currentBillIndex: saved.currentBillIndex ?? -1,
            billData: saved.billData ?? {},
            billFileIdMapping: saved.billFileIdMapping ?? {},
            mergedBills: saved.mergedBills ?? {},
            billNavigation: saved.billNavigation ?? [],
            duplicatesMap: saved.duplicatesMap ?? {},
            duplicateResolutions: normalizeRestoredResolutions(saved.duplicateResolutions, saved.duplicatesMap),
            rejectedPairSuggestions: saved.rejectedPairSuggestions ?? {},
            entityNameOverrides: saved.entityNameOverrides ?? {},
            lenMergeSelection: saved.lenMergeSelection ?? [],
            lenMergeUndoStack: [],  // Never restored — undo doesn't survive refresh
            saMergeSelection: saved.saMergeSelection ?? [],
            saMergeUndoStack: [],   // Never restored
            meterMergeSelection: saved.meterMergeSelection ?? [],
            meterMergeUndoStack: [], // Never restored
            previewBillFileId: saved.previewBillFileId ?? null,
            fastTrackMode: saved.fastTrackMode ?? 'off',
            fastTrackAnalysis: saved.fastTrackAnalysis ?? null,
            fastTrackSkippedSteps: saved.fastTrackSkippedSteps ?? [],
            fastTrackEnergyPlanChoice: saved.fastTrackEnergyPlanChoice ?? null,
          });

          // Rebuild _billFiles from billFileIdMapping if saved billData has entries
          // Also restore bill data onto the BillFile objects
          const billFileIdMapping = saved.billFileIdMapping ?? {};
          const savedBillData = saved.billData ?? {};
          for (const [groupId, billFileId] of Object.entries(billFileIdMapping)) {
            const bf = store._billFiles.get(billFileId);
            if (bf && savedBillData[groupId]) {
              bf.data = savedBillData[groupId];
            }
          }

          // Fallback: if billFileIdMapping was empty/stale but we have bills and a matchRequest,
          // rebuild the mapping by matching bills to match request entries by date
          if (Object.keys(billFileIdMapping).length === 0 && bills.length > 0 && saved.matchRequest) {
            const rebuilt = rebuildBillFileIdMapping(saved.matchRequest, bills);
            if (Object.keys(rebuilt.billFileIdMapping).length > 0) {
              patchState(store, {
                billFileIdMapping: rebuilt.billFileIdMapping,
                billData: rebuilt.billData
              });
              // Store the BillFile objects
              for (const [, billFile] of Object.entries(rebuilt.billFilesMap)) {
                store._billFiles.set(billFile.id, billFile);
              }
              console.log(`[BillAiFlowStore] Rebuilt billFileIdMapping from dates: ${Object.keys(rebuilt.billFileIdMapping).length} matches`);
            }
          }

          // Rebuild bill navigation if we had progressed past the meter step
          if (saved.completedSteps?.includes('meter')) {
            const resp = store.matchResponse();
            const req = store.matchRequest();
            if (resp && req) {
              const nav = buildBillNavigationList(resp, req, store.billFileIdMapping(), store._billFiles, store.billData(), store._billService.pairedGroups());
              patchState(store, { billNavigation: nav, currentBillIndex: 0 });
            }
            // Restore bill index after rebuilding navigation
            const navLen = store.billNavigation().length;
            if (saved.currentBillIndex != null && saved.currentBillIndex >= 0 && saved.currentBillIndex < navLen) {
              patchState(store, { currentBillIndex: saved.currentBillIndex });
            }
          }

          // Persist the pruned session so the deleted bills don't come back
          // on the next restore.
          if (prunedOnRestore) {
            (store as any).saveState();
          }

          return; // Successfully restored
        } catch (error) {
          console.error('Error restoring bill AI flow state:', error);
          (store as any).clearPersistedState();
        }
      }

      // Pre-populate selectedCustomerId from context if available
      // (at CUSTOMER/LEN/SERVICE_ADDRESS/METER levels, the customer is already known)
      if (context.customerId) {
        patchState(store, {
          selectedCustomerId: context.customerId,
          selectedCustomerLabel: context.customerName || `Customer ${context.customerId}`
        });
      }

      // Default: start from the beginning. All levels (including METER) start on
      // intro — its auto-scan runs the match API and is what populates bill
      // navigation; non-portfolio levels then auto-advance past it.
      patchState(store, { currentStep: 'intro' });
      // Note: no saveState() here — fresh init has no matchRequest yet,
      // so doSave() will skip anyway. Save happens after runMatch().
    },

    // =========================================================================
    // Bill Data
    // =========================================================================

    updateBillData(groupId: string, data: ParsedBillData): void {
      patchState(store, {
        billData: { ...store.billData(), [groupId]: data }
      });

      // Sync to BillService so the dashboard hierarchy stays current
      const billFileId = store.billFileIdMapping()[groupId];
      if (billFileId) {
        store._billService.syncBillDataFromWizard(billFileId, data);
      }
      (store as any).saveState();
    },

    getBillData(groupId: string): ParsedBillData | null {
      return store.billData()[groupId] ?? null;
    },

    setMergedBill(groupId: string, mergedIntoGroupId: string): void {
      patchState(store, {
        mergedBills: { ...store.mergedBills(), [groupId]: mergedIntoGroupId }
      });
      (store as any).saveState();
    },

    // =========================================================================
    // BillFile Lookup
    // =========================================================================

    getBillFile(billFileId: string): BillFile | null {
      return store._billFiles.get(billFileId) ?? null;
    },

    getBillFileForGroup(groupId: string): BillFile | null {
      const billFileId = store.billFileIdMapping()[groupId];
      if (!billFileId) return null;
      return store._billFiles.get(billFileId) ?? null;
    },

    getCurrentBillFile(): BillFile | null {
      const idx = store.currentBillIndex();
      const nav = store.billNavigation();
      if (idx >= 0 && idx < nav.length && nav[idx].billFileId) {
        return store._billFiles.get(nav[idx].billFileId!) ?? null;
      }
      return null;
    },

    getProcessedBills(): BillFile[] {
      const result: BillFile[] = [];
      store._billFiles.forEach(bf => {
        if (bf.data) result.push(bf);
      });
      return result;
    },

    getApprovedBills(): BillFile[] {
      const result: BillFile[] = [];
      store._billFiles.forEach(bf => {
        if (bf.status === BillStatus.APPROVED && bf.data) result.push(bf);
      });
      return result;
    },

    getBills(): BillFile[] {
      return Array.from(store._billFiles.values());
    },

    // =========================================================================
    // Entity Edit Data
    // =========================================================================

    getMeterEdit(groupId: string): MeterEditData {
      return store.meterEdits()[groupId] || {};
    },

    updateMeterEdit(groupId: string, data: Partial<MeterEditData>): void {
      const current = store.meterEdits();
      patchState(store, {
        meterEdits: {
          ...current,
          [groupId]: { ...(current[groupId] || {}), ...data }
        }
      });
      (store as any).saveState();
    },

    getSaEdit(groupId: string): SaEditData {
      return store.saEdits()[groupId] || {};
    },

    updateSaEdit(groupId: string, data: Partial<SaEditData>): void {
      const current = store.saEdits();
      patchState(store, {
        saEdits: {
          ...current,
          [groupId]: { ...(current[groupId] || {}), ...data }
        }
      });
      (store as any).saveState();
    },

    initSaEditFromRequest(groupId: string): SaEditData {
      const existing = store.saEdits()[groupId];
      if (existing) return existing;

      const request = store.matchRequest();
      if (!request) return {};

      for (const len of request.legalEntities) {
        for (const sa of len.serviceAddresses) {
          if (sa.groupId === groupId) {
            const edit: SaEditData = {
              street1: sa.street1 || '',
              street2: sa.street2 || '',
              city: sa.city || '',
              state: sa.state || '',
              zip: sa.zip || '',
            };
            const current = store.saEdits();
            patchState(store, {
              saEdits: { ...current, [groupId]: { ...(current[groupId] || {}), ...edit } }
            });
            (store as any).saveState();
            return edit;
          }
        }
      }
      return {};
    },

    initMeterEditFromRequest(groupId: string): MeterEditData {
      const existing = store.meterEdits()[groupId];
      if (existing) return existing;

      const request = store.matchRequest();
      if (!request) return {};

      for (const len of request.legalEntities) {
        for (const sa of len.serviceAddresses) {
          for (const meter of sa.meters) {
            if (meter.groupId === groupId) {
              const edit: MeterEditData = {
                utilityAccountNumber1: meter.utilityAccountNumber1 || '',
                utilityAccountNumber2: meter.utilityAccountNumber2 || '',
                utilityAccountNumber3: meter.utilityAccountNumber3 || '',
                utilityId: meter.utilityId || null,
                commodityType: meter.commodityType || CommodityType.Electricity,
                street2: meter.meterAddress2 || '',
              };
              const current = store.meterEdits();
              patchState(store, {
                meterEdits: { ...current, [groupId]: { ...(current[groupId] || {}), ...edit } }
              });
              (store as any).saveState();
              return edit;
            }
          }
        }
      }
      return {};
    },

    // =========================================================================
    // Selection Management
    // =========================================================================

    getSelection(groupId: string): number | string | null {
      return store.entitySelections()[groupId] ?? null;
    },

    updateSelection(groupId: string, value: number | string | null): void {
      patchState(store, {
        entitySelections: { ...store.entitySelections(), [groupId]: value },
        selectionsDirtySinceMatch: true
      });
      (store as any).saveState();
    },

    getOptions(match: MatchSuggestion | null, alternatives: MatchSuggestion[], extractedName: string): SelectOption[] {
      const options: SelectOption[] = [
        { value: 'new_record', label: `Create New`, isCreateNew: true }
      ];

      if (match) {
        options.push({
          value: match.id,
          label: `${match.label} (${Math.round(match.score * 100)}% match)`,
          score: match.score
        });
      }

      for (const alt of alternatives || []) {
        if (!match || alt.id !== match.id) {
          options.push({
            value: alt.id,
            label: `${alt.label} (${Math.round(alt.score * 100)}% match)`,
            score: alt.score
          });
        }
      }

      return options;
    },

    getLenBillingAddress(groupId: string): string {
      const request = store.matchRequest();
      if (!request) return '';

      for (const len of request.legalEntities) {
        if (len.groupId === groupId && len.billingAddress) {
          return [len.billingAddress.street1, len.billingAddress.city, len.billingAddress.state, len.billingAddress.zip].filter(Boolean).join(', ');
        }
      }
      return '';
    },

    getOriginalName(groupId: string): string {
      // Check user overrides first
      const override = store.entityNameOverrides()[groupId];
      if (override !== undefined) return override;

      const request = store.matchRequest();
      if (!request) return 'Unknown';

      for (const len of request.legalEntities) {
        if (len.groupId === groupId) return len.name || 'Unknown';
        for (const sa of len.serviceAddresses) {
          if (sa.groupId === groupId) {
            return [sa.street1, sa.city, sa.state].filter(Boolean).join(', ') || 'Unknown';
          }
          for (const meter of sa.meters) {
            if (meter.groupId === groupId) return meter.utilityAccountNumber1 || 'Unknown';
          }
        }
      }
      return 'Unknown';
    },

    /** Updates the display name for an entity (LEN, SA, or meter). */
    updateEntityName(groupId: string, name: string): void {
      patchState(store, {
        entityNameOverrides: { ...store.entityNameOverrides(), [groupId]: name }
      });
      (store as any).saveState();
    },

    /**
     * Patch the billing address for a LEN in the match request (shallow merge —
     * partial updates preserve existing fields). The billing address is only
     * sent to the backend when creating a NEW LEN; existing-LEN links ignore
     * this data in buildImportRequest because the DB record is authoritative.
     *
     * Without this method, address edits stayed in the LEN component's local
     * `lenAddressDataMap` and buildImportRequest would send the original
     * extracted address (or empty fields) to the backend, causing import
     * failures for LENs where the user filled in missing fields like zip.
     */
    updateLenBillingAddress(groupId: string, address: {
      street1?: string;
      street2?: string;
      city?: string;
      state?: string;
      zip?: string;
    }): void {
      const request = store.matchRequest();
      if (!request) {
        console.warn(
          '[BillAiFlowStore] updateLenBillingAddress called with no matchRequest — edit not persisted',
          { groupId, address }
        );
        return;
      }
      const updatedLens = request.legalEntities.map(len => {
        if (len.groupId !== groupId) return len;
        return {
          ...len,
          billingAddress: {
            ...(len.billingAddress ?? {}),
            ...address,
          },
        };
      });
      patchState(store, {
        matchRequest: { ...request, legalEntities: updatedLens }
      });
      (store as any).saveState();
    },

    // =========================================================================
    // LEN Merge
    // =========================================================================

    toggleLenMergeSelection(groupId: string): void {
      const current = store.lenMergeSelection();
      if (current.includes(groupId)) {
        patchState(store, { lenMergeSelection: current.filter(id => id !== groupId) });
      } else {
        patchState(store, { lenMergeSelection: [...current, groupId] });
      }
      (store as any).saveState();
    },

    clearLenMergeSelection(): void {
      patchState(store, { lenMergeSelection: [] });
      (store as any).saveState();
    },

    mergeLens(targetGroupId: string): void {
      const mergeIds = store.lenMergeSelection();
      if (mergeIds.length < 2) return;

      const response = store.matchResponse();
      const request = store.matchRequest();
      if (!response || !request) return;

      const targetLen = response.legalEntities.find(l => l.groupId === targetGroupId);
      if (!targetLen) {
        console.warn('[BillAiFlowStore] mergeLens: target LEN not found:', targetGroupId);
        return;
      }

      const undoSnapshot = createUndoSnapshot(response, store.entitySelections(), 'LEN merge', request);
      if (!undoSnapshot) return;

      const removedGroupIds = response.legalEntities
        .filter(l => mergeIds.includes(l.groupId) && l.groupId !== targetGroupId)
        .map(l => l.groupId);

      // Collapse both lists — the request too, or computeIdenticalLenGroups
      // keeps seeing the merged-away LEN and its duplicate error stays stuck.
      const updatedEntities = collapseLenList(response.legalEntities, targetGroupId, removedGroupIds);
      const updatedRequestLens = collapseLenList(request.legalEntities, targetGroupId, removedGroupIds);

      // Clean up selections for removed groups
      const sel = { ...store.entitySelections() };
      for (const id of removedGroupIds) {
        delete sel[id];
      }

      // Sync merged entity names to BillService for dashboard hierarchy
      const overrides = store.entityNameOverrides();
      const targetName = overrides[targetGroupId]
        ?? request.legalEntities.find(l => l.groupId === targetGroupId)?.name
        ?? targetGroupId;
      const removedNames = removedGroupIds.map(id =>
        overrides[id] ?? request.legalEntities.find(l => l.groupId === id)?.name ?? id
      );
      store._billService.mergeLenNamesInBills(removedNames, targetName);

      patchState(store, {
        matchResponse: { ...response, legalEntities: updatedEntities },
        matchRequest: { ...request, legalEntities: updatedRequestLens },
        entitySelections: sel,
        lenMergeSelection: [],
        lenMergeUndoStack: pushUndoSnapshot(store.lenMergeUndoStack(), undoSnapshot)
      });
      (store as any).saveState();
    },

    /** Undo the last LEN merge. Note: BillService entity names are NOT reverted —
     *  the dashboard hierarchy may be temporarily out of sync until the import step
     *  rebuilds everything from matchResponse. */
    undoLastLenMerge(): void {
      const stack = store.lenMergeUndoStack();
      if (stack.length === 0) return;

      const prev = stack[stack.length - 1];
      patchState(store, {
        matchResponse: prev.matchResponse,
        matchRequest: prev.matchRequest,
        entitySelections: prev.entitySelections,
        lenMergeUndoStack: stack.slice(0, -1),
        lenMergeSelection: []
      });
      (store as any).saveState();
    },

    // =========================================================================
    // SA Merge
    // =========================================================================

    toggleSaMergeSelection(groupId: string): void {
      const current = store.saMergeSelection();
      if (current.includes(groupId)) {
        patchState(store, { saMergeSelection: current.filter(id => id !== groupId) });
      } else {
        patchState(store, { saMergeSelection: [...current, groupId] });
      }
      (store as any).saveState();
    },

    clearSaMergeSelection(): void {
      patchState(store, { saMergeSelection: [] });
      (store as any).saveState();
    },

    mergeSas(targetGroupId: string): boolean {
      const mergeIds = store.saMergeSelection();
      if (mergeIds.length < 2) {
        console.warn('[BillAiFlowStore] mergeSas: needs >=2 selections, got', mergeIds.length);
        return false;
      }

      const response = store.matchResponse();
      const request = store.matchRequest();
      if (!response || !request) return false;

      const undoSnapshot = createUndoSnapshot(response, store.entitySelections(), 'SA merge', request);
      if (!undoSnapshot) return false;

      const targetFound = response.legalEntities.some(len =>
        len.serviceAddresses.some(sa => sa.groupId === targetGroupId)
      );
      if (!targetFound) {
        console.warn('[BillAiFlowStore] mergeSas: target SA not found:', targetGroupId);
        return false;
      }
      const removedGroupIds = mergeIds.filter(id => id !== targetGroupId);

      // Collapse both lists — the request too, or a later re-match (which
      // reuses the request) would resurrect the merged-away SAs.
      const updatedEntities = collapseSaList(response.legalEntities, targetGroupId, removedGroupIds);
      const updatedRequestLens = collapseSaList(request.legalEntities, targetGroupId, removedGroupIds);

      // Clean up selections for removed groups
      const sel = { ...store.entitySelections() };
      for (const id of removedGroupIds) {
        delete sel[id];
      }

      patchState(store, {
        matchResponse: { ...response, legalEntities: updatedEntities },
        matchRequest: { ...request, legalEntities: updatedRequestLens },
        entitySelections: sel,
        saMergeSelection: [],
        saMergeUndoStack: pushUndoSnapshot(store.saMergeUndoStack(), undoSnapshot)
      });
      (store as any).saveState();
      return true;
    },

    undoLastSaMerge(): void {
      const stack = store.saMergeUndoStack();
      if (stack.length === 0) return;

      const prev = stack[stack.length - 1];
      patchState(store, {
        matchResponse: prev.matchResponse,
        matchRequest: prev.matchRequest,
        entitySelections: prev.entitySelections,
        saMergeUndoStack: stack.slice(0, -1),
        saMergeSelection: []
      });
      (store as any).saveState();
    },

    // =========================================================================
    // Meter Merge
    // =========================================================================

    toggleMeterMergeSelection(groupId: string): void {
      const current = store.meterMergeSelection();
      if (current.includes(groupId)) {
        patchState(store, { meterMergeSelection: current.filter(id => id !== groupId) });
      } else {
        patchState(store, { meterMergeSelection: [...current, groupId] });
      }
      (store as any).saveState();
    },

    clearMeterMergeSelection(): void {
      patchState(store, { meterMergeSelection: [] });
      (store as any).saveState();
    },

    mergeMeters(targetGroupId: string): void {
      const mergeIds = store.meterMergeSelection();
      if (mergeIds.length < 2) return;

      const response = store.matchResponse();
      const request = store.matchRequest();
      if (!response || !request) return;

      const undoSnapshot = createUndoSnapshot(response, store.entitySelections(), 'meter merge', request);
      if (!undoSnapshot) return;

      const targetFound = response.legalEntities
        .flatMap(len => len.serviceAddresses)
        .flatMap(sa => sa.meters)
        .some(meter => meter.groupId === targetGroupId);
      if (!targetFound) {
        console.warn('[BillAiFlowStore] mergeMeters: target meter not found:', targetGroupId);
        return;
      }
      const removedGroupIds = mergeIds.filter(id => id !== targetGroupId);

      // Never merge across commodities — a dual-fuel split (elec + gas meters
      // sharing a UAN) is intentional, and merging would import gas bills under
      // an electric meter identity (e.g. therms stored as kWh).
      const meterCommodityOf = (groupId: string): CommodityType => {
        const edited = store.meterEdits()[groupId]?.commodityType;
        if (edited) return edited;
        for (const len of request.legalEntities) {
          for (const sa of len.serviceAddresses) {
            const m = sa.meters.find(mm => mm.groupId === groupId);
            if (m) return m.commodityType || CommodityType.Electricity;
          }
        }
        return CommodityType.Electricity;
      };
      const targetCommodity = meterCommodityOf(targetGroupId);
      const crossCommodityIds = mergeIds.filter(id => id !== targetGroupId && meterCommodityOf(id) !== targetCommodity);
      if (crossCommodityIds.length > 0) {
        console.warn('[BillAiFlowStore] mergeMeters: blocked cross-commodity merge into', targetGroupId, 'from', crossCommodityIds);
        return;
      }

      // Collapse both lists — the request too, or a later re-match (which
      // reuses the request) would resurrect the merged-away meters.
      const updatedEntities = collapseMeterList(response.legalEntities, targetGroupId, removedGroupIds);
      const updatedRequestLens = collapseMeterList(request.legalEntities, targetGroupId, removedGroupIds);

      // Clean up selections for removed groups
      const sel = { ...store.entitySelections() };
      for (const id of removedGroupIds) {
        delete sel[id];
      }

      patchState(store, {
        matchResponse: { ...response, legalEntities: updatedEntities },
        matchRequest: { ...request, legalEntities: updatedRequestLens },
        entitySelections: sel,
        meterMergeSelection: [],
        meterMergeUndoStack: pushUndoSnapshot(store.meterMergeUndoStack(), undoSnapshot)
      });
      (store as any).saveState();
    },

    undoLastMeterMerge(): void {
      const stack = store.meterMergeUndoStack();
      if (stack.length === 0) return;

      const prev = stack[stack.length - 1];
      patchState(store, {
        matchResponse: prev.matchResponse,
        matchRequest: prev.matchRequest,
        entitySelections: prev.entitySelections,
        meterMergeUndoStack: stack.slice(0, -1),
        meterMergeSelection: []
      });
      (store as any).saveState();
    },

    // =========================================================================
    // LEN Delete
    // =========================================================================

    deleteLens(): void {
      const deleteIds = store.lenMergeSelection();
      if (deleteIds.length === 0) return;

      const response = store.matchResponse();
      const request = store.matchRequest();
      if (!response || !request) return;

      const undoSnapshot = createUndoSnapshot(response, store.entitySelections(), 'LEN delete', request);
      if (!undoSnapshot) return; // Abort — destructive operation requires undo safety net

      const updatedEntities = response.legalEntities.filter(l => !deleteIds.includes(l.groupId));
      // Drop them from the request too, same reason as mergeLens.
      const updatedRequestLens = request.legalEntities.filter(l => !deleteIds.includes(l.groupId));

      // Clean up selections for removed groups and their children
      const sel = { ...store.entitySelections() };
      for (const len of response.legalEntities) {
        if (deleteIds.includes(len.groupId)) {
          delete sel[len.groupId];
          for (const sa of len.serviceAddresses) {
            delete sel[sa.groupId];
            for (const meter of sa.meters) {
              delete sel[meter.groupId];
            }
          }
        }
      }

      patchState(store, {
        matchResponse: { ...response, legalEntities: updatedEntities },
        matchRequest: { ...request, legalEntities: updatedRequestLens },
        entitySelections: sel,
        lenMergeSelection: [],
        lenDeleteUndoStack: pushUndoSnapshot(store.lenDeleteUndoStack(), undoSnapshot)
      });
      (store as any).saveState();
    },

    undoLastLenDelete(): void {
      const stack = store.lenDeleteUndoStack();
      if (stack.length === 0) return;

      const prev = stack[stack.length - 1];
      patchState(store, {
        matchResponse: prev.matchResponse,
        matchRequest: prev.matchRequest,
        entitySelections: prev.entitySelections,
        lenDeleteUndoStack: stack.slice(0, -1),
        lenMergeSelection: []
      });
      (store as any).saveState();
    },

    // =========================================================================
    // SA Delete
    // =========================================================================

    deleteSas(): void {
      const deleteIds = store.saMergeSelection();
      if (deleteIds.length === 0) return;

      const response = store.matchResponse();
      const request = store.matchRequest();
      if (!response || !request) return;

      const undoSnapshot = createUndoSnapshot(response, store.entitySelections(), 'SA delete', request);
      if (!undoSnapshot) return; // Abort — destructive operation requires undo safety net

      // Clean up selections of the deleted SAs and their meters
      const sel = { ...store.entitySelections() };
      for (const len of response.legalEntities) {
        for (const sa of len.serviceAddresses) {
          if (deleteIds.includes(sa.groupId)) {
            delete sel[sa.groupId];
            for (const meter of sa.meters) {
              delete sel[meter.groupId];
            }
          }
        }
      }

      // Drop them from the request too, same reason as mergeSas.
      const updatedEntities = dropSasFromList(response.legalEntities, deleteIds);
      const updatedRequestLens = dropSasFromList(request.legalEntities, deleteIds);

      patchState(store, {
        matchResponse: { ...response, legalEntities: updatedEntities },
        matchRequest: { ...request, legalEntities: updatedRequestLens },
        entitySelections: sel,
        saMergeSelection: [],
        saDeleteUndoStack: pushUndoSnapshot(store.saDeleteUndoStack(), undoSnapshot)
      });
      (store as any).saveState();
    },

    undoLastSaDelete(): void {
      const stack = store.saDeleteUndoStack();
      if (stack.length === 0) return;

      const prev = stack[stack.length - 1];
      patchState(store, {
        matchResponse: prev.matchResponse,
        matchRequest: prev.matchRequest,
        entitySelections: prev.entitySelections,
        saDeleteUndoStack: stack.slice(0, -1),
        saMergeSelection: []
      });
      (store as any).saveState();
    },

    // =========================================================================
    // Meter Delete
    // =========================================================================

    deleteMeters(): void {
      const deleteIds = store.meterMergeSelection();
      if (deleteIds.length === 0) return;

      const response = store.matchResponse();
      const request = store.matchRequest();
      if (!response || !request) return;

      const undoSnapshot = createUndoSnapshot(response, store.entitySelections(), 'meter delete', request);
      if (!undoSnapshot) return; // Abort — destructive operation requires undo safety net

      // Clean up selections of the deleted meters
      const sel = { ...store.entitySelections() };
      for (const id of deleteIds) {
        delete sel[id];
      }

      // Drop them from the request too, same reason as mergeMeters.
      const updatedEntities = dropMetersFromList(response.legalEntities, deleteIds);
      const updatedRequestLens = dropMetersFromList(request.legalEntities, deleteIds);

      patchState(store, {
        matchResponse: { ...response, legalEntities: updatedEntities },
        matchRequest: { ...request, legalEntities: updatedRequestLens },
        entitySelections: sel,
        meterMergeSelection: [],
        meterDeleteUndoStack: pushUndoSnapshot(store.meterDeleteUndoStack(), undoSnapshot)
      });
      (store as any).saveState();
    },

    undoLastMeterDelete(): void {
      const stack = store.meterDeleteUndoStack();
      if (stack.length === 0) return;

      const prev = stack[stack.length - 1];
      patchState(store, {
        matchResponse: prev.matchResponse,
        matchRequest: prev.matchRequest,
        entitySelections: prev.entitySelections,
        meterDeleteUndoStack: stack.slice(0, -1),
        meterMergeSelection: []
      });
      (store as any).saveState();
    },

    // =========================================================================
    // LEN / SA Cascade
    // =========================================================================

    /** Check if a LEN or SA entity is new (no selection made) */
    isNewEntity(groupId: string): boolean {
      const sel = store.entitySelections()[groupId];
      return sel === null || sel === undefined;
    },


    // =========================================================================
    // Bill Navigation
    // =========================================================================

    buildBillNavigation(): void {
      const response = store.matchResponse();
      const request = store.matchRequest();
      if (!response || !request) {
        patchState(store, { billNavigation: [], currentBillIndex: -1 });
        (store as any).saveState();
        return;
      }

      const nav = buildBillNavigationList(response, request, store.billFileIdMapping(), store._billFiles, store.billData(), store._billService.pairedGroups());
      patchState(store, { billNavigation: nav, currentBillIndex: 0 });
      (store as any).saveState();
    },

    getBillNavigation(): BillNavEntry[] {
      return store.billNavigation();
    },

    setCurrentBillIndex(index: number): void {
      patchState(store, { currentBillIndex: index });
      (store as any).saveState();
    },

    getCurrentBillNav(): BillNavEntry | null {
      const idx = store.currentBillIndex();
      return store.billNavigation()[idx] ?? null;
    },

    navigateToNextBill(): boolean {
      const idx = store.currentBillIndex();
      if (idx < store.billNavigation().length - 1) {
        patchState(store, { currentBillIndex: idx + 1 });
        (store as any).saveState();
        return true;
      }
      return false;
    },

    navigateToPrevBill(): boolean {
      const idx = store.currentBillIndex();
      if (idx > 0) {
        patchState(store, { currentBillIndex: idx - 1 });
        (store as any).saveState();
        return true;
      }
      return false;
    },

    getTotalBills(): number {
      return store.billNavigation().length;
    },

    getCurrentMeterGroupId(): string | null {
      const idx = store.currentBillIndex();
      const nav = store.billNavigation();
      return (idx >= 0 && idx < nav.length) ? nav[idx].meterGroupId : null;
    },

    getTotalMeters(): number {
      const meterIds = new Set(store.billNavigation().map(n => n.meterGroupId));
      return meterIds.size;
    },

    getCurrentMeterNumber(): number {
      const idx = store.currentBillIndex();
      const nav = store.billNavigation();
      if (idx < 0 || idx >= nav.length) return 0;
      const current = nav[idx];
      const uniqueMeters = Array.from(new Set(nav.map(n => n.meterGroupId)));
      return uniqueMeters.indexOf(current.meterGroupId) + 1;
    },

    getBillsForCurrentMeter(): BillNavEntry[] {
      const idx = store.currentBillIndex();
      const nav = store.billNavigation();
      if (idx < 0 || idx >= nav.length) return [];
      const current = nav[idx];
      return nav.filter(n => n.meterGroupId === current.meterGroupId);
    },

    // =========================================================================
    // Preview
    // =========================================================================

    setPreviewBillFile(billFile: BillFile | null): void {
      if (billFile) {
        store._billFiles.set(billFile.id, billFile);
        patchState(store, { previewBillFileId: billFile.id });
      } else {
        patchState(store, { previewBillFileId: null });
      }
      (store as any).saveState();
    },

    getPreviewBillFile(): BillFile | null {
      const id = store.previewBillFileId();
      if (!id) return null;
      return store._billFiles.get(id) ?? null;
    },

    /** Scenario C: register an already-imported bill's downloaded PDF as the
     *  current bill's pair so the review step shows both document tabs. */
    attachServerPairBill(billGroupId: string, billFile: BillFile, pairedFileIsUtility: boolean): void {
      store._billFiles.set(billFile.id, billFile);
      const nav = store.billNavigation().map(n => n.billGroupId === billGroupId
        ? { ...n, isDualBill: true, pairedBillFileId: billFile.id, pairedFileIsUtility }
        : n);
      patchState(store, { billNavigation: nav });
    },

    // =========================================================================
    // Match API
    // =========================================================================

    async runMatch(customerId: number | null = null, opts: { reuseRequest?: boolean } = {}): Promise<void> {
      const processedBills: BillFile[] = [];
      store._billFiles.forEach(bf => {
        if (bf.data) processedBills.push(bf);
      });

      if (processedBills.length === 0) {
        patchState(store, { error: 'No bills with parsed data available' });
        return;
      }

      patchState(store, { isLoading: true, isMatching: true, error: null });

      try {
        // Mid-flow re-matches reuse the current request: it carries the user's
        // structural edits (LEN/SA/meter merges and deletes) that a rebuild
        // from raw bills would silently discard — and it keeps groupIds stable
        // so selections and edits stay keyed to the right rows.
        const priorRequest = opts.reuseRequest ? store.matchRequest() : null;
        let request: BillImportMatchRequest;
        let result: ReturnType<typeof buildMatchRequest> | null = null;
        if (priorRequest) {
          request = JSON.parse(JSON.stringify(priorRequest)) as BillImportMatchRequest;
          request.customerId = customerId;
          clearCommittedIds(request);
        } else {
          result = buildMatchRequest(processedBills, customerId, store._billService.pairedGroups());
          request = result.request;
        }
        // Committed picks ride along so the backend can short-circuit those
        // rows and narrow their children's candidate pools.
        applySelectionsToRequest(request, store.entitySelections());
        // Entry-level context ids too — the first /match runs before any
        // selections exist, and it must already be parent-scoped.
        const context = store._context();
        if (context) {
          applyContextIdsToRequest(request, context);
        }

        // Store bill data and file mappings (unchanged when reusing — the
        // request keeps the same bill groupIds).
        patchState(store, {
          matchRequest: request,
          ...(result ? { billData: result.billData, billFileIdMapping: result.billFileIdMapping } : {})
        });

        // Store BillFile objects in the private map
        result?.billFilesMap.forEach((bf, id) => {
          store._billFiles.set(id, bf);
        });

        const response = await firstValueFrom(store._billImportService.match(request));
        if (!response) {
          patchState(store, { error: 'Match API returned empty response' });
          return;
        }
        // Committed rows come back with match: null — restore their display
        // data from the previous response so linked rows don't go blank.
        const prevResponse = store.matchResponse();
        if (prevResponse) {
          preserveCommittedMatchData(response, prevResponse, store.entitySelections());
        }
        patchState(store, { matchResponse: response });

        // Seed saEdits / meterEdits from matchRequest for every groupId in the
        // response. The UI calls init*FromRequest lazily when each step opens,
        // but analyzeFastTrack runs now and its validators read *Edits — without
        // this seed every new-SA/new-meter field looks empty and Fast Track
        // blocks on phantom "required field(s) missing" errors. Existing
        // entries win so prior user edits aren't clobbered on re-match.
        //
        // utilityId is a special case: the client doesn't know it, the server
        // resolves it and returns it on matchResponse.meter.utility. This
        // mirrors initAllMeters() in the meter step component — if you add a
        // server-resolved field to initAllMeters, mirror it here or Fast Track
        // will block on it.
        {
          const currentSaEdits = { ...store.saEdits() };
          const currentMeterEdits = { ...store.meterEdits() };
          const respUtilityByMeter = new Map<string, number | null>();
          // Only a confident suggestion is seeded — the meter step's dropdown still lets the user change it.
          const respRateClassByMeter = new Map<string, { rateScheduleId: number; utilityId: number | null }>();
          const respMeterIds = new Set<string>();
          for (const respLen of response.legalEntities) {
            for (const respSa of respLen.serviceAddresses) {
              for (const respMeter of respSa.meters) {
                if (!respMeter.groupId) {
                  console.warn('[BillAiFlowStore] runMatch seed: response meter without groupId, skipped', respMeter);
                  continue;
                }
                respMeterIds.add(respMeter.groupId);
                if (respMeter.utility?.utilityId != null) {
                  respUtilityByMeter.set(respMeter.groupId, respMeter.utility.utilityId);
                }
                if (respMeter.rateClass?.isConfident && respMeter.rateClass.rateScheduleId != null) {
                  respRateClassByMeter.set(respMeter.groupId, {
                    rateScheduleId: respMeter.rateClass.rateScheduleId,
                    utilityId: respMeter.rateClass.utilityId ?? null,
                  });
                }
              }
            }
          }
          const reqMeterIds = new Set<string>();
          for (const reqLen of request.legalEntities) {
            for (const reqSa of reqLen.serviceAddresses) {
              if (!reqSa.groupId) {
                console.warn('[BillAiFlowStore] runMatch seed: request SA without groupId, skipped', reqSa);
              } else if (!currentSaEdits[reqSa.groupId]) {
                currentSaEdits[reqSa.groupId] = {
                  street1: reqSa.street1 || '',
                  street2: reqSa.street2 || '',
                  city: reqSa.city || '',
                  state: reqSa.state || '',
                  zip: reqSa.zip || '',
                };
              }
              for (const reqMeter of reqSa.meters) {
                if (!reqMeter.groupId) {
                  console.warn('[BillAiFlowStore] runMatch seed: request meter without groupId, skipped', reqMeter);
                  continue;
                }
                reqMeterIds.add(reqMeter.groupId);
                const existingEdit = currentMeterEdits[reqMeter.groupId];
                // A suggested schedule is only usable under the meter's own, known utility.
                const suggestionFor = (utilityId: number | null | undefined): number | null => {
                  const sugg = respRateClassByMeter.get(reqMeter.groupId);
                  if (!sugg || utilityId == null) return null;
                  return (sugg.utilityId == null || sugg.utilityId === utilityId) ? sugg.rateScheduleId : null;
                };
                if (!existingEdit) {
                  const utilityId = reqMeter.utilityId ?? respUtilityByMeter.get(reqMeter.groupId) ?? null;
                  const suggestion = suggestionFor(utilityId);
                  currentMeterEdits[reqMeter.groupId] = {
                    utilityAccountNumber1: reqMeter.utilityAccountNumber1 || '',
                    utilityAccountNumber2: reqMeter.utilityAccountNumber2 || '',
                    utilityAccountNumber3: reqMeter.utilityAccountNumber3 || '',
                    utilityId,
                    rateClassId: suggestion,
                    rateClassUserSet: false,
                    commodityType: reqMeter.commodityType || CommodityType.Electricity,
                    street2: reqMeter.meterAddress2 || '',
                  };
                } else if (!(existingEdit.rateClassUserSet ?? (existingEdit.rateClassId != null))) {
                  // Re-match with a rate class the user never picked: follow the new suggestion (e.g.
                  // the meter now resolves to an existing record whose own schedule wins), or clear a
                  // stale one. State saved before the flag existed only held user picks, hence the fallback.
                  const suggestion = suggestionFor(existingEdit.utilityId);
                  if ((existingEdit.rateClassId ?? null) !== suggestion) {
                    currentMeterEdits[reqMeter.groupId] = { ...existingEdit, rateClassId: suggestion, rateClassUserSet: false };
                  }
                }
              }
            }
          }
          // Flag request/response meter drift so a backend contract regression
          // doesn't silently cause Fast Track to pause with a misleading reason.
          const missingInResp = [...reqMeterIds].filter(id => !respMeterIds.has(id));
          const extraInResp = [...respMeterIds].filter(id => !reqMeterIds.has(id));
          if (missingInResp.length || extraInResp.length) {
            console.warn('[BillAiFlowStore] runMatch seed: meter groupId drift between request and response', {
              missingInResponse: missingInResp,
              unexpectedInResponse: extraInResp,
            });
          }
          patchState(store, { saEdits: currentSaEdits, meterEdits: currentMeterEdits });
        }

        if (!customerId) {
          patchState(store, { customerMatch: response.customer ?? null });

          // Auto-select best match if high confidence (same threshold as
          // initializeSelections and fast-track analyzer — see
          // utils/match-score.util.ts AUTO_ACCEPT_SCORE).
          const best = response.customer?.match;
          if (shouldAutoAcceptMatch(best)) {
            patchState(store, {
              selectedCustomerId: best!.id,
              selectedCustomerLabel: best!.label
            });
          }
        }

        // Extract duplicates from match response
        const duplicatesMap: Record<string, DuplicateWarning> = {};
        for (const len of response.legalEntities) {
          for (const sa of len.serviceAddresses) {
            for (const meter of sa.meters) {
              for (const bill of meter.bills) {
                if (bill.duplicate) {
                  duplicatesMap[bill.groupId] = bill.duplicate;
                }
              }
            }
          }
        }
        patchState(store, { duplicatesMap });

        // Initialize entity selections, preserving explicit user picks
        const selections = mergeSelections(store.entitySelections(), initializeSelections(response));
        // Exact duplicates can never be created — a prior "create new" pick
        // must not survive a row turning exact (e.g. after a data edit).
        forceExactSelections(selections, response);
        patchState(store, { entitySelections: selections, selectionsDirtySinceMatch: false });

        (store as any).saveState();
      } catch (err: any) {
        console.error('[BillAiFlowStore] runMatch failed:', err);
        const errorMsg = err?.error?.message || err?.message || err?.statusText || 'Failed to match entities';
        const statusCode = err?.status ? ` (${err.status})` : '';
        patchState(store, { error: `${errorMsg}${statusCode}` });
      } finally {
        patchState(store, { isLoading: false, isMatching: false });
      }
    },

    /**
     * Whether a re-match would change anything: selections were committed
     * since the last match and an existing customer is selected to match
     * against. Callers re-fire runMatch so downstream steps see candidate
     * pools narrowed to the committed parents. (Guard + call live apart
     * because same-block store methods cannot reference each other.)
     */
    needsRematch(): boolean {
      return store.selectionsDirtySinceMatch() && store.selectedCustomerId() !== null;
    },

    /**
     * Re-anchor the flow at the customer level so cross-entity blocked rows
     * can link where they live. Clears the entry-point parent commitments and
     * all selections; the caller re-runs the match afterwards (same-block
     * store methods cannot reference each other).
     */
    migrateContextToCustomerLevel(): void {
      const ctx = store._context();
      if (!ctx) return;
      store._context.set({
        ...ctx,
        level: 'CUSTOMER',
        lenId: undefined,
        serviceAddressId: undefined,
        meterId: undefined,
        lenName: undefined,
        serviceAddress: undefined,
        utilityAccountNumber: undefined,
      });
      patchState(store, {
        steps: getStepsForLevel('CUSTOMER'),
        entitySelections: {},
        selectionsDirtySinceMatch: false,
        completedSteps: ['intro'],
        currentStep: 'len',
      });
    },


    // =========================================================================
    // Fast Track
    // =========================================================================

    analyzeFastTrack(): FastTrackAnalysis {
      const response = store.matchResponse();
      const context = store._context();
      const level = context?.level || 'PORTFOLIO';

      const result: FastTrackAnalysis = {
        canSkipCustomer: false,
        canSkipLen: false,
        canSkipSa: false,
        canSkipMeter: false,
        canSkipBillReview: false,
        skipBlockReasons: {},
        blockingGroupIds: {},
        allOnUtility: true,
        supplierBillCount: 0,
        highConfidenceSupplierBills: 0,
        needsEnergyPlanBills: 0,
        hasBlockingDuplicates: false,
        isEligible: false,
        canFullyAutomate: false,
        unappliedMatchWarnings: [],
      };

      if (!response) return result;

      try {

      // --- Customer step ---
      if (level !== 'PORTFOLIO') {
        result.canSkipCustomer = true; // customer already known
      } else {
        const customerMatch = store.customerMatch();
        if (shouldAutoAcceptMatch(customerMatch?.match)) {
          result.canSkipCustomer = true;
        } else {
          result.skipBlockReasons['customer'] = 'Customer match needs review';
        }
      }

      // --- Hierarchy levels: LEN, SA, Meter ---
      let allLensOk = true;
      let allSasOk = true;
      let allMetersOk = true;

      // Track per-row pause fragments for every entity step so the banner
      // names the specific row(s) and cause. These feed both the
      // skipBlockReasons string and blockingGroupIds (for row highlighting).
      const lenBlockers: PauseBlocker[] = [];
      const saBlockers: PauseBlocker[] = [];
      const meterBlockers: PauseBlocker[] = [];
      // Meter blockers can be match-ambiguity OR duplicate-bill; only the former
      // get the verify-or-link ask, and its singular/plural tracks this count
      // (not meterBlockers.length, which also includes duplicate-bill blockers).
      let ambiguousMeterCount = 0;
      const unappliedMatchWarnings: UnappliedMatchWarning[] = [];
      const nameLookupRequest = store.matchRequest();
      const labelFor = (groupId: string): string =>
        nameLookupRequest ? getOriginalNameFromRequest(nameLookupRequest, groupId) : 'Unknown';

      // Cascade rule (see FAST-TRACK.md): once any parent in the chain is
      // going to be created new, no descendant can match an existing record,
      // so the analyzer must skip match-ambiguity checks for the subtree.
      // Starts with the customer — if a new customer is being created
      // upfront, every LEN/SA/Meter below is unambiguously new.
      const customerIsNew = store.isNewCustomer();

      // Helper: record a "we saw a displayable match but chose not to auto-
      // apply it" note so the summary step can surface it as a warning.
      const noteUnappliedMatch = (
        kind: UnappliedMatchWarning['entityKind'],
        entityLabel: string,
        match: { label: string; score: number } | null,
      ) => {
        if (match != null && match.score >= MIN_DISPLAY_SCORE && match.score < AUTO_ACCEPT_SCORE) {
          unappliedMatchWarnings.push({
            entityKind: kind,
            entityLabel,
            bestMatchLabel: match.label,
            // floor (not round) so a 0.949 score never renders as "95%"
            // alongside a "creating new" outcome — that would lie about the
            // reason the score didn't clear the auto-accept threshold.
            bestMatchScorePct: Math.floor(match.score * 100),
          });
        }
      };

      // Customer-level unapplied-match note: if the user is going to create
      // a new customer but the match API returned a candidate that looks
      // plausible, surface it on the summary step so they can reconsider.
      // The match is defensive duplicate-detection (it searches by LEN name
      // across the org, since customer names are user-chosen labels) — the
      // cascade shouldn't silently eat that signal.
      if (customerIsNew) {
        const custMatch = store.customerMatch()?.match;
        if (custMatch != null && custMatch.score >= MIN_DISPLAY_SCORE && custMatch.score < AUTO_ACCEPT_SCORE) {
          unappliedMatchWarnings.push({
            entityKind: 'customer',
            entityLabel: '',
            bestMatchLabel: custMatch.label,
            bestMatchScorePct: Math.floor(custMatch.score * 100),
          });
        }
      }

      // The cascade is driven by the user's SELECTION state, not the raw
      // match score. Selection state is initialized from the match (confident
      // → match id, else null for create-new) but can be changed by the user
      // mid-flow. Driving cascade off `selections[groupId] == null` means
      // re-running this analyzer after a selection change correctly flips
      // pause banners and row outlines for the affected subtree.
      // (store.isNewEntity() isn't callable from within the same
      // withMethods block — duplicate the logic via entitySelections.)
      const selectionsSnapshot = store.entitySelections();
      const selectionIsNew = (groupId: string): boolean =>
        isNewSelection(selectionsSnapshot[groupId]);

      for (const len of response.legalEntities) {
        const lenSelectionIsNew = selectionIsNew(len.groupId);

        // Defensive duplicate signal: when the user will create new AND the
        // parent scope (customer) is itself an existing entity, surface any
        // weak-but-displayable match on the summary step so the user can
        // catch a mistaken new-create. SCOPE NOTE: if the customer is new,
        // any LEN match returned by the API is from some OTHER customer's
        // data — physically unreachable from the current scope — so the
        // "link instead" advice wouldn't be actionable. Suppress the
        // warning in that case. noteUnappliedMatch's internal score-band
        // filter still applies for the ones we do emit.
        if (!customerIsNew && lenSelectionIsNew) {
          noteUnappliedMatch('legal entity', labelFor(len.groupId), len.match);
        }

        // A LEN blocks fast-track only when (a) the parent chain isn't new,
        // AND (b) the user will create new for this row (not auto-accepted
        // or manually picked), AND (c) there's a displayable option to
        // consider. A <MIN_DISPLAY_SCORE match with no alternatives is a
        // no-op choice — UI just creates new, no point pausing.
        if (!customerIsNew && lenSelectionIsNew && hasDisplayableOptions(len.match, len.alternatives)) {
          allLensOk = false;
          lenBlockers.push({
            groupId: len.groupId,
            fragment: buildAmbiguousMatchFragment(labelFor(len.groupId), len.match),
          });
        }

        // Cascade "subtree is new" when parent is new OR this row's
        // selection is new. Reflects the user's current decisions, so
        // re-analysis after a selection change flips the cascade for the
        // affected subtree.
        const lenSubtreeIsNew = customerIsNew || lenSelectionIsNew;

        for (const sa of len.serviceAddresses) {
          const saSelectionIsNew = selectionIsNew(sa.groupId);
          // Same scope-validity reasoning as LEN above: if the LEN subtree
          // is new, SA match candidates come from OTHER LENs (or other
          // customers) and aren't linkable from within this subtree.
          if (!lenSubtreeIsNew && saSelectionIsNew) {
            noteUnappliedMatch('service address', labelFor(sa.groupId), sa.match);
          }
          // Cross-entity duplicate: the address exists under a different LEN.
          if (sa.crossParentMatch) {
            allSasOk = false;
            saBlockers.push({
              groupId: sa.groupId,
              fragment: `Address "${labelFor(sa.groupId)}" already exists under a different legal entity — migrate the import or edit the entry`,
            });
          }
          if (!lenSubtreeIsNew && saSelectionIsNew && hasDisplayableOptions(sa.match, sa.alternatives)) {
            allSasOk = false;
            saBlockers.push({
              groupId: sa.groupId,
              fragment: buildAmbiguousMatchFragment(labelFor(sa.groupId), sa.match),
            });
          }

          const saSubtreeIsNew = lenSubtreeIsNew || saSelectionIsNew;

          for (const meter of sa.meters) {
            const meterSelectionIsNew = selectionIsNew(meter.groupId);
            if (!saSubtreeIsNew && meterSelectionIsNew) {
              noteUnappliedMatch('meter', labelFor(meter.groupId), meter.match);
            }
            // Cross-entity duplicate: the account number exists under a
            // different SA on this customer. Blocking — resolve by migrating
            // the import to the customer level or editing the entry.
            if (meter.crossParentMatch) {
              allMetersOk = false;
              meterBlockers.push({
                groupId: meter.groupId,
                fragment: `Meter "${labelFor(meter.groupId)}" already exists under a different entity — migrate the import or edit the entry`,
              });
            }
            if (!saSubtreeIsNew && meterSelectionIsNew && hasDisplayableOptions(meter.match, meter.alternatives)) {
              allMetersOk = false;
              ambiguousMeterCount++;
              meterBlockers.push({
                groupId: meter.groupId,
                fragment: buildAmbiguousMatchFragment(labelFor(meter.groupId), meter.match),
              });
            }

            // Blocking duplicates only apply when the bill will land on an
            // existing meter (see computeNewMeterBillGroupIds). One fragment
            // per meter.
            const meterEffectivelyNew = saSubtreeIsNew || meterSelectionIsNew;
            let meterHasBlockingDuplicate = false;
            for (const bill of meter.bills) {
              if (bill.duplicate && (!bill.duplicate.pairable || store.rejectedPairSuggestions()[bill.groupId]) && !meterEffectivelyNew) {
                const ot = bill.duplicate.overlapType;
                if (ot === 'PARTIAL' || ot === 'CONTAINS' || ot === 'CONTAINED_BY') {
                  result.hasBlockingDuplicates = true;
                  meterHasBlockingDuplicate = true;
                }
              }

              // A skipped duplicate isn't imported, so it needs no energy plan
              if (store.skippedBillGroupIds().has(bill.groupId)) continue;

              // Supplier analysis
              if (bill.supplier?.supplierId != null) {
                const supplier = bill.supplier;
                result.allOnUtility = false;
                result.supplierBillCount++;
                if (supplier.confidence >= SUPPLIER_AUTO_ACCEPT_SCORE && supplier.energyPlanId != null) {
                  result.highConfidenceSupplierBills++;
                } else {
                  result.needsEnergyPlanBills++;
                }
              }
            }
            if (meterHasBlockingDuplicate && !meterBlockers.some(b => b.groupId === meter.groupId)) {
              meterBlockers.push({
                groupId: meter.groupId,
                fragment: `Meter "${labelFor(meter.groupId)}" has a bill that overlaps with an existing bill — resolve the duplicate before importing`,
              });
            }
          }
        }
      }

      // LEN step (skip if level already past LEN)
      if (level === 'LEN' || level === 'SERVICE_ADDRESS' || level === 'METER') {
        result.canSkipLen = true;
      } else if (allLensOk) {
        result.canSkipLen = true;
      } else {
        result.skipBlockReasons['len'] = joinPauseReason(lenBlockers, LEN_KIND, lenBlockers.length);
        result.blockingGroupIds['len'] = lenBlockers.map(b => b.groupId);
      }

      // SA step
      if (level === 'SERVICE_ADDRESS' || level === 'METER') {
        result.canSkipSa = true;
      } else if (allSasOk) {
        result.canSkipSa = true;
      } else {
        result.skipBlockReasons['sa'] = joinPauseReason(saBlockers, SA_KIND, saBlockers.length);
        result.blockingGroupIds['sa'] = saBlockers.map(b => b.groupId);
      }

      // Meter step
      if (level === 'METER') {
        result.canSkipMeter = true;
      } else if (allMetersOk && !result.hasBlockingDuplicates) {
        result.canSkipMeter = true;
      } else {
        result.skipBlockReasons['meter'] = joinPauseReason(meterBlockers, METER_KIND, ambiguousMeterCount);
        result.blockingGroupIds['meter'] = meterBlockers.map(b => b.groupId);
      }

      // Bill review step
      if (result.allOnUtility) {
        result.canSkipBillReview = true;
      } else if (result.supplierBillCount > 0 && result.needsEnergyPlanBills === 0) {
        // All supplier bills have high confidence AND known energy plan
        result.canSkipBillReview = true;
      } else {
        result.skipBlockReasons['bill-review'] = result.needsEnergyPlanBills > 0
          ? `${result.needsEnergyPlanBills} bill(s) need energy plan decisions`
          : 'Bills need review';
      }

      // --- Required field validation (final override) ---
      // Run all four step validators, even for steps already marked skippable
      // above, and block fast-track for any step with missing required fields.
      // This is a safety net: match confidence alone doesn't guarantee the
      // extracted data is complete enough to import.
      const isNewEntity = (gid: string) => {
        const sel = store.entitySelections()[gid];
        return sel === null || sel === undefined;
      };
      const lenAddressMap: Record<string, LenAddress> = {};
      const extractedLenNames: Record<string, string> = {};
      const req = store.matchRequest();
      if (req) {
        for (const len of req.legalEntities) {
          if (len.groupId) {
            lenAddressMap[len.groupId] = {
              street1: len.billingAddress?.street1 ?? '',
              street2: len.billingAddress?.street2 ?? '',
              city: len.billingAddress?.city ?? '',
              state: len.billingAddress?.state ?? '',
              zip: len.billingAddress?.zip ?? '',
            };
            if (len.name) extractedLenNames[len.groupId] = len.name;
          }
        }
      }
      const lenValidation = validateLenStep({
        legalEntities: response.legalEntities,
        entityNameOverrides: store.entityNameOverrides(),
        extractedLenNames,
        lenAddressDataMap: lenAddressMap,
        isNewEntity,
        selections: store.entitySelections(),
      });
      if (lenValidation.hasBlockers) {
        result.canSkipLen = false;
        result.skipBlockReasons['len'] = buildFieldPauseReason(lenValidation.errors, LEN_KIND);
        // Merge (don't overwrite) so rows that blocked for ambiguous-match
        // reasons above still get their .needs-attention outline.
        result.blockingGroupIds['len'] = Array.from(new Set([
          ...(result.blockingGroupIds['len'] ?? []),
          ...lenValidation.errors.map(e => e.entityId),
        ]));
      }

      const saValidation = validateSaStep({
        legalEntities: response.legalEntities,
        saEdits: store.saEdits(),
        isNewEntity,
      });
      if (saValidation.hasBlockers) {
        result.canSkipSa = false;
        result.skipBlockReasons['sa'] = buildFieldPauseReason(saValidation.errors, SA_KIND);
        // Merge (don't overwrite) so rows that blocked for ambiguous-match
        // reasons above still get their .needs-attention outline.
        result.blockingGroupIds['sa'] = Array.from(new Set([
          ...(result.blockingGroupIds['sa'] ?? []),
          ...saValidation.errors.map(e => e.entityId),
        ]));
      }

      const meterValidation = validateMeterStep({
        legalEntities: response.legalEntities,
        meterEdits: store.meterEdits(),
        isNewEntity,
      });
      if (meterValidation.hasBlockers) {
        result.canSkipMeter = false;
        result.skipBlockReasons['meter'] = buildFieldPauseReason(meterValidation.errors, METER_KIND);
        // Merge (don't overwrite) so rows that blocked for ambiguous-match
        // reasons or duplicate bills above still get their outline.
        result.blockingGroupIds['meter'] = Array.from(new Set([
          ...(result.blockingGroupIds['meter'] ?? []),
          ...meterValidation.errors.map(e => e.entityId),
        ]));
      }

      const isBudgetingMode = store._billService.isBudgetingMode();
      const billValidation = validateBillReviewStep({
        billData: store.billData(),
        billNavigation: store.billNavigation(),
        excludeBillGroupIds: store.skippedBillGroupIds(),
        isNewEntity,
        // Thread per-bill meter context so the date-range / rate-bound / TX-demand
        // rules run in fast-track too (parity with the interactive review step).
        meterContextFor: (nav) => {
          const ctx = resolveMeterContext({
            meterGroupId: nav.meterGroupId,
            response: store.matchResponse(),
            request: store.matchRequest(),
            saEdits: store.saEdits(),
          });
          return {
            commodityType: ctx?.commodityType,
            isBudgetingMode,
          };
        },
      });
      if (billValidation.hasBlockers) {
        result.canSkipBillReview = false;
        result.skipBlockReasons['bill-review'] = `${billValidation.errors.length} required field(s) missing`;
      }
      // Rate-bound warnings don't block the interactive step, but skipping it
      // would import an out-of-range rate sight-unseen — pause for review.
      const rateWarnings = billValidation.warnings
        .filter(w => w.field === 'supplyCharges' || w.field === 'totalAmount');
      if (rateWarnings.length && result.canSkipBillReview) {
        result.canSkipBillReview = false;
        result.skipBlockReasons['bill-review'] = `${rateWarnings.length} rate warning(s) to review`;
      }

      // Determine which steps are in the current flow
      const steps = store.steps();
      const stepKeys = steps.map(s => s.key);
      const canSkipMap: Record<string, boolean> = {
        'customer': result.canSkipCustomer,
        'len': result.canSkipLen,
        'sa': result.canSkipSa,
        'meter': result.canSkipMeter,
        'bill-review': result.canSkipBillReview,
      };

      // isEligible: at least one non-intro/non-summary/non-bill-review step in the flow can be skipped
      const skippableSteps = stepKeys.filter(k => k !== 'intro' && k !== 'summary' && k !== 'bill-review' && canSkipMap[k]);
      result.isEligible = skippableSteps.length > 0;

      // canFullyAutomate: ALL steps before bill-review can be auto-completed
      const reviewableSteps = stepKeys.filter(k => k !== 'intro' && k !== 'summary' && k !== 'bill-review');
      result.canFullyAutomate = reviewableSteps.every(k => canSkipMap[k] === true);

      // Weak-but-displayable matches we chose not to auto-apply or pause on.
      // Surfaced on the summary step via fastTrackWarnings.
      result.unappliedMatchWarnings = unappliedMatchWarnings;

      console.info('[FastTrack] analysis', {
        isEligible: result.isEligible,
        canFullyAutomate: result.canFullyAutomate,
        canSkip: {
          customer: result.canSkipCustomer,
          len: result.canSkipLen,
          sa: result.canSkipSa,
          meter: result.canSkipMeter,
          'bill-review': result.canSkipBillReview,
        },
        skipBlockReasons: result.skipBlockReasons,
      });

      patchState(store, { fastTrackAnalysis: result });
      (store as any).saveState();

      return result;
      } catch (err) {
        // Safety net: if any validator or downstream logic throws, return a
        // conservative analysis so the user is routed through manual review
        // instead of getting stuck on an error screen. Log so bugs surface.
        console.error('[BillAiFlowStore] analyzeFastTrack failed; falling back to manual review:', err);
        result.skipBlockReasons = { ...result.skipBlockReasons, customer: 'Fast-track analysis failed' };
        result.blockingGroupIds = {};
        result.isEligible = false;
        result.canFullyAutomate = false;
        patchState(store, { fastTrackAnalysis: result });
        return result;
      }
    },

    /**
     * Execute fast track mode — auto-complete skippable steps, apply supplier data,
     * resolve duplicates, and navigate to the appropriate landing step.
     *
     * Returns a promise that resolves when the fast track animation completes.
     * The staggered delays create a visual cascade effect in the step header.
     */
    async executeFastTrack(): Promise<void> {
      const analysis = store.fastTrackAnalysis();
      if (!analysis || !analysis.isEligible) return;

      patchState(store, { fastTrackMode: 'fast', isLoading: true });

      try {
      const steps = store.steps();
      const stepKeys = steps.map(s => s.key);
      const skippedSteps: BillAiFlowStep[] = [];

      const canSkipMap: Record<string, boolean> = {
        'customer': analysis.canSkipCustomer,
        'len': analysis.canSkipLen,
        'sa': analysis.canSkipSa,
        'meter': analysis.canSkipMeter,
        'bill-review': analysis.canSkipBillReview,
      };

      // Complete skippable steps with staggered animation
      for (const step of stepKeys) {
        if (step === 'intro' || step === 'summary') continue;
        if (!canSkipMap[step]) continue;

        // Stagger for visual effect
        await new Promise(r => setTimeout(r, 300));

        const completed = store.completedSteps();
        if (!completed.includes(step)) {
          patchState(store, { completedSteps: [...completed, step] });
        }
        skippedSteps.push(step as BillAiFlowStep);
      }

      patchState(store, { fastTrackSkippedSteps: skippedSteps });

      // Auto-resolve EXACT/NEAR_EXACT duplicates
      const response = store.matchResponse();
      if (response) {
        const duplicatesMap: Record<string, import('../../../models/bill-import.models').DuplicateWarning> = {};
        for (const len of response.legalEntities) {
          for (const sa of len.serviceAddresses) {
            for (const meter of sa.meters) {
              for (const bill of meter.bills) {
                if (bill.duplicate) {
                  duplicatesMap[bill.groupId] = bill.duplicate;
                }
              }
            }
          }
        }
        patchState(store, { duplicatesMap });
      }

      // Auto-resolve non-blocking duplicates (unresolvedDuplicates is
      // EXACT/NEAR_EXACT-only and excludes bills on new meters)
      const resolutions = { ...store.duplicateResolutions() };
      for (const { billGroupId, duplicate } of store.unresolvedDuplicates()) {
        resolutions[billGroupId] = {
          billGroupId,
          existingBillId: duplicate.existingBillId,
          action: 'SKIP'
        };
      }
      patchState(store, { duplicateResolutions: resolutions });

      // Build bill navigation
      const matchResponse = store.matchResponse();
      const matchRequest = store.matchRequest();
      if (matchResponse && matchRequest) {
        const nav = buildBillNavigationList(matchResponse, matchRequest, store.billFileIdMapping(), store._billFiles, store.billData(), store._billService.pairedGroups());
        patchState(store, { billNavigation: nav, currentBillIndex: 0 });
      }

      // Auto-apply supplier data from match response onto billData
      if (matchResponse) {
        const billData = { ...store.billData() };
        for (const len of matchResponse.legalEntities) {
          for (const sa of len.serviceAddresses) {
            for (const meter of sa.meters) {
              for (const bill of meter.bills) {
                const data = billData[bill.groupId];
                if (!data) continue;
                if (bill.supplier?.supplierId != null) {
                  const supplier = bill.supplier;
                  billData[bill.groupId] = {
                    ...data,
                    supplierId: supplier.supplierId,
                    providerName: supplier.supplierName,
                    energyPlanId: supplier.energyPlanId,
                  };
                }
              }
            }
          }
        }
        patchState(store, { billData });
      }

      // Auto-resolve energy plans for supplier bills on existing meters
      let customerId: number | null = null;
      const ctxCustId = store._context()?.customerId;
      if (ctxCustId) {
        customerId = parseInt(ctxCustId, 10);
      } else {
        const sel = store.selectedCustomerId();
        if (sel != null) {
          customerId = typeof sel === 'number' ? sel : parseInt(String(sel), 10);
        }
      }

      if (customerId && !isNaN(customerId)) {
        const epResult = await autoResolveEnergyPlans(
          store.matchResponse()!,
          store.matchRequest()!,
          store.entitySelections(),
          store.billData(),
          customerId,
          store._contractsV4Service,
          store.skippedBillGroupIds(),
        );
        if (epResult.resolved > 0) {
          console.log(`Express import: auto-resolved ${epResult.resolved} energy plan(s)`);
          patchState(store, { billData: epResult.updatedBillData });

          // If all bills that needed energy plans got resolved, bill-review can be skipped
          if (analysis.needsEnergyPlanBills > 0 && analysis.needsEnergyPlanBills === epResult.resolved) {
            canSkipMap['bill-review'] = true;
          }
        }
      }

      // Determine landing step
      await new Promise(r => setTimeout(r, 300));

      // Mark intro and any newly-skippable steps as completed
      const completed = [...store.completedSteps()];
      if (!completed.includes('intro')) {
        completed.push('intro');
      }
      if (canSkipMap['bill-review'] && !completed.includes('bill-review')) {
        completed.push('bill-review');
        skippedSteps.push('bill-review' as BillAiFlowStep);
      }
      patchState(store, { completedSteps: completed, fastTrackSkippedSteps: skippedSteps });

      // Check if there's a non-skippable step before summary
      const firstNonSkippable = stepKeys.find(k =>
        k !== 'intro' && k !== 'summary' && !canSkipMap[k]
      );
      const landingStep = firstNonSkippable || 'summary';
      console.info('[FastTrack] landing', {
        landingStep,
        reason: firstNonSkippable
          ? (analysis.skipBlockReasons?.[firstNonSkippable as BillAiFlowStep] ?? 'unknown')
          : 'nothing to resolve',
      });
      patchState(store, { currentStep: landingStep as BillAiFlowStep });

      (store as any).saveState();
      } catch (err: any) {
        console.error('Express import execution failed:', err);
        patchState(store, {
          fastTrackMode: 'off' as any,
        });
      } finally {
        patchState(store, { isLoading: false });
      }
    },

    /**
     * Exit fast track mode — revert to manual review.
     * Removes auto-completed steps and navigates to the first relevant step.
     */
    exitFastTrack(): void {
      const skipped = store.fastTrackSkippedSteps();
      const completed = store.completedSteps().filter(s => !skipped.includes(s as BillAiFlowStep));

      patchState(store, {
        fastTrackMode: 'review',
        completedSteps: completed,
        fastTrackSkippedSteps: [],
      });

      // Navigate to the first non-completed step after intro
      const steps = store.steps();
      const firstIncomplete = steps.find(s =>
        s.key !== 'intro' && s.key !== 'summary' && !completed.includes(s.key)
      );
      patchState(store, { currentStep: firstIncomplete?.key || 'intro' });
      (store as any).saveState();
    },

    setFastTrackMode(mode: 'off' | 'fast' | 'review'): void {
      patchState(store, { fastTrackMode: mode });
      (store as any).saveState();
    },

    setFastTrackEnergyPlanChoice(choice: 'now' | 'later' | null): void {
      patchState(store, { fastTrackEnergyPlanChoice: choice });
      (store as any).saveState();
    },

    // =========================================================================
    // Import Request Builder
    // =========================================================================

    buildImportRequest(): BillImportRequest {
      const response = store.matchResponse();
      const request = store.matchRequest();
      const selections = store.entitySelections();
      const customerId = store.selectedCustomerId();
      const currentBillData = store.billData();
      const currentMergedBills = store.mergedBills();
      const isBudgetingMode = store._billService.isBudgetingMode();
      const skippedGroupIds = store.skippedBillGroupIds();
      const replacedGroupIds = store.replacedBillGroupIds();

      if (!response || !request) {
        throw new Error('Cannot build import request without match data');
      }
      if (customerId === null) {
        throw new Error('Customer must be selected before import');
      }

      const legalEntities: LegalEntityImport[] = [];

      for (const lenResult of response.legalEntities) {
        const lenSelection = selections[lenResult.groupId];
        const lenRequest = request.legalEntities.find(l => l.groupId === lenResult.groupId);

        const serviceAddresses: ServiceAddressImport[] = [];

        for (const saResult of lenResult.serviceAddresses) {
          const saSelection = selections[saResult.groupId];
          const saRequest = lenRequest?.serviceAddresses.find(s => s.groupId === saResult.groupId);
          const saEdit = store.saEdits()[saResult.groupId] || {};

          const meters: MeterImport[] = [];

          for (const meterResult of saResult.meters) {
            const meterSelection = selections[meterResult.groupId];
            const meterRequest = saRequest?.meters.find(m => m.groupId === meterResult.groupId);
            const meterEdit = store.meterEdits()[meterResult.groupId] || {};

            const bills: BillImport[] = [];
            // Gas meters carry their bills' native unit (therms/Dth/CCF…); the
            // first bill with a resolvable unit wins. Electricity stays kWh server-side.
            let meterUnitId: number | undefined;

            for (const billResult of meterResult.bills) {
              const data = currentBillData[billResult.groupId];
              if (!data) continue;
              // Check if this bill was merged into another (dual billing — skip the supplier copy)
              if (currentMergedBills[billResult.groupId]) continue;
              // A skipped duplicate is left out entirely, with its files
              if (skippedGroupIds.has(billResult.groupId)) continue;
              // The meter carries one unit and bills none, so the first imported bill's unit
              // wins; a later bill edited to a different unit cannot be represented.
              const billUnitId = data.usageUnitId ?? gasUnitIdFromName(data.usageUnit);
              if (meterUnitId === undefined) {
                meterUnitId = billUnitId;
              } else if (billUnitId !== undefined && billUnitId !== meterUnitId) {
                console.warn('[BillAiFlowStore] Bill', billResult.groupId, 'uses unit', billUnitId,
                  'but its meter was already assigned unit', meterUnitId, '— the meter unit applies');
              }

              // Check if this is a dual bill by looking at nav entries
              const navEntry = store.billNavigation().find(n => n.billGroupId === billResult.groupId);
              const isDual = navEntry?.isDualBill || false;

              const billFileId = store.billFileIdMapping()[billResult.groupId];
              const billFile = billFileId ? store._billFiles.get(billFileId) : null;
              // Same-session dual pair: the supplier document rides along as a
              // second PDF. Scenario C pairs get a synthetic 'existing-*' file
              // with no tempFileId, so they are naturally excluded here.
              const pairedFile = navEntry?.pairedBillFileId && !navEntry.pairedFileIsUtility
                ? store._billFiles.get(navEntry.pairedBillFileId)
                : null;
              if (!billFile?.tempFileId) {
                // Without tempFileId the backend silently skips PDF attachment.
                console.error(
                  '[BillAiFlowStore] buildImportRequest: tempFileId missing for bill',
                  billResult.groupId,
                  billFileId ? '(billFile not in _billFiles)' : '(no billFileIdMapping entry)'
                );
              }
              if (navEntry?.pairedBillFileId && !navEntry.pairedFileIsUtility && !pairedFile?.tempFileId) {
                // Same silent-skip risk for the pair's supplier document.
                console.error(
                  '[BillAiFlowStore] buildImportRequest: paired supplier bill has no tempFileId for bill',
                  billResult.groupId, '— supplier PDF will not be attached'
                );
              }

              // A switched-off side is omitted entirely — sending zeros makes
              // the backend compute a 0 rate and reject the bill.
              let supplyOff = isSupplyDisabled(data);
              let distOff = isDistributionDisabled(data);

              // The API flagged this bill as the missing half of an existing
              // dual bill — import merges it into that bill. Only this bill's
              // own side is sent; the other side (prefilled into the form for
              // display) already lives on the existing bill. Bills on meters
              // the user forced to "create new" never pair — the existing bill
              // lives on the old meter (same suppression as getBillDuplicate).
              const dup = store.newMeterBillGroupIds().has(billResult.groupId)
                ? undefined : store.duplicatesMap()[billResult.groupId];
              const pairWithBillId = dup?.pairable && !store.rejectedPairSuggestions()[billResult.groupId]
                ? dup.existingBillId : undefined;
              if (pairWithBillId) {
                supplyOff = dup!.pairSide === 'DISTRIBUTION';
                distOff = !supplyOff;
              }

              const billImport: BillImport = {
                groupId: billResult.groupId,
                billStartDate: data.billingPeriodStart,
                billEndDate: data.billingPeriodEnd,
                totalAmount: data.totalAmount || 0,
                totalUsage: data.usageAmount || 0,
                demand: data.demandKw,
                // Time-of-use kWh — unset on gas bills and on electricity bills without a breakdown
                onPeakUsage: data.onPeakUsage,
                intermediateUsage: data.intermediateUsage,
                offPeakUsage: data.offPeakUsage,
                onPeakCost: data.onPeakCost,
                intermediateCost: data.intermediateCost,
                offPeakCost: data.offPeakCost,
                // Supply
                supplyTotal: supplyOff ? undefined : data.supplyTotalCost || 0,
                supplyCharges: supplyOff ? undefined : data.supplyCharges || 0,
                supplyTax: supplyOff ? undefined : data.supplyTaxes || 0,
                contractCharges: supplyOff ? undefined : data.supplyContractCharges?.total,
                energyCharges: supplyOff ? undefined : data.supplyEnergyCharges?.total,
                capacityCharges: supplyOff ? undefined : data.supplyCapacityCharges?.total,
                transmissionCharges: supplyOff ? undefined : data.supplyTransmissionCharges?.total,
                marketCharges: supplyOff ? undefined : data.supplyMarketCharges?.total,
                lineLossCharges: supplyOff ? undefined : data.supplyLineLossCharges?.total,
                supplyGrtSut: supplyOff ? undefined : data.supplyGrtSut?.total,
                supplyMisc: supplyOff ? undefined : data.supplyMiscCharges?.total,
                // Supply — gas-only buckets (undefined on electricity bills)
                supplyCustomerCharges: supplyOff ? undefined : data.supplyCustomerCharges?.total,
                supplyGasCharges: supplyOff ? undefined : data.supplyGasCharges?.total,
                supplyOffPeakCharges: supplyOff ? undefined : data.supplyOffPeakCharges?.total,
                supplyRateClassCharges: supplyOff ? undefined : data.supplyRateClassCharges?.total,
                // Distribution
                distributionTotal: distOff ? undefined : data.distributionTotalCost || 0,
                distributionCharges: distOff ? undefined : data.distributionCharges || 0,
                distributionTax: distOff ? undefined : data.distributionTaxes || 0,
                distributionCustomerCharges: distOff ? undefined : data.distributionCustomerCharges?.total,
                distributionEnergyCharges: distOff ? undefined : data.distributionEnergyCharges?.total,
                distributionDemandCharges: distOff ? undefined : data.distributionDemandCharges?.total,
                distributionMisc: distOff ? undefined : data.distributionMiscCharges?.total,
                // Distribution — gas-only buckets (undefined on electricity bills)
                distributionGasCharges: distOff ? undefined : data.distributionGasCharges?.total,
                distributionDeliveryCharges: distOff ? undefined : data.distributionDeliveryCharges?.total,
                distributionOffPeakCharges: distOff ? undefined : data.distributionOffPeakCharges?.total,
                distributionBalancingCharges: distOff ? undefined : data.distributionBalancingCharges?.total,
                distributionRateClassCharges: distOff ? undefined : data.distributionRateClassCharges?.total,
                distributionGrtSut: distOff ? undefined : data.distributionGrtSut?.total,
                supplyLineItems: supplyOff ? undefined : lineItemsForImport(data, SUPPLY_BUCKET_KEYS, isBudgetingMode),
                distributionLineItems: distOff ? undefined : lineItemsForImport(data, DISTRIBUTION_BUCKET_KEYS, isBudgetingMode),
                // Provider
                providerName: data.providerName || undefined,
                supplierId: data.supplierId || undefined,
                energyPlanId: data.energyPlanId,
                // File
                tempFileId: billFile?.tempFileId,
                supplierTempFileId: pairedFile?.tempFileId,
                // This document's extraction (on a same-session pair the utility one, else the supplier's);
                // a pair merge into an existing bill only links it when it has no link yet
                billAiId: billFile?.billAiId ?? pairedFile?.billAiId,
                pairWithBillId,
                // An exact duplicate the user chose to replace: the server deletes that bill
                replaceBillId: replacedGroupIds.has(billResult.groupId) ? dup?.existingBillId : undefined,
                // Type: a bill carrying only one side, or one the review step
                // treats as a dual half (isDualHalf — same rule as the radio and
                // validation), is one half of dual billing — the other side
                // arrives on a separate physical bill.
                billType: isDual || isDualHalf(data) || pairWithBillId != null || supplyOff !== distOff ? 'DUAL' : 'CONSOLIDATED',
              };
              bills.push(billImport);
            }

            if (bills.length === 0) continue;

            const resolvedCommodityType = meterEdit.commodityType || meterRequest?.commodityType;
            if (!resolvedCommodityType) {
              // Defaulting silently to Electricity here would misclassify gas meters
              // without any user-visible signal — surface it so we can diagnose drift.
              console.error(
                '[BillAiFlowStore] buildImportRequest: commodityType missing for meter',
                meterResult.groupId,
                '— defaulting to ELECTRICITY'
              );
            }
            meters.push({
              groupId: meterResult.groupId,
              id: (meterSelection && meterSelection !== 'new_record') ? Number(meterSelection) : undefined,
              utilityAccountNumber1: meterEdit.utilityAccountNumber1 || meterRequest?.utilityAccountNumber1,
              utilityAccountNumber2: meterEdit.utilityAccountNumber2 || meterRequest?.utilityAccountNumber2,
              utilityAccountNumber3: meterEdit.utilityAccountNumber3 || meterRequest?.utilityAccountNumber3,
              utilityId: meterEdit.utilityId ?? meterResult.utility?.utilityId ?? undefined,
              commodityType: resolvedCommodityType || CommodityType.Electricity,
              unitId: (resolvedCommodityType || CommodityType.Electricity) === CommodityType.Gas ? meterUnitId : undefined,
              // `rateClassId: undefined` — the server (`BillAiImportService.applyMeterRateClasses`)
              // treats a missing `rateClassId` as "don't touch" and never clears an existing value.
              // Always omit when the user hasn't chosen one; never send `null` (would imply "clear").
              rateClassId: meterEdit.rateClassId ?? undefined,
              // The meter step's Address 2 input; the API reads it as meterAddress2 (MeterInput), not street2.
              // An edit wins even when cleared (a blank must not fall back to the extracted value); a blank is omitted.
              meterAddress2: (meterEdit.street2 ?? meterRequest?.meterAddress2) || undefined,
              bills
            });
          }

          if (meters.length === 0) continue;

          serviceAddresses.push({
            groupId: saResult.groupId,
            id: (saSelection && saSelection !== 'new_record') ? Number(saSelection) : undefined,
            label: saEdit.label?.trim()
              || (saEdit.street1 ? `${saEdit.street1}, ${saEdit.city || ''} ${saEdit.state || ''}`.trim() : saRequest?.label),
            street1: saEdit.street1 || saRequest?.street1,
            street2: saEdit.street2 || saRequest?.street2,
            city: saEdit.city || saRequest?.city,
            state: saEdit.state || saRequest?.state,
            zip: saEdit.zip || saRequest?.zip,
            meters
          });
        }

        if (serviceAddresses.length === 0) continue;

        const lenId = (lenSelection && lenSelection !== 'new_record') ? String(lenSelection) : undefined;

        const lenNameOverride = store.entityNameOverrides()[lenResult.groupId];

        legalEntities.push({
          groupId: lenResult.groupId,
          id: lenId || undefined,
          name: lenNameOverride ?? lenRequest?.name,
          // Don't send billing address when linking to an existing LEN — the existing address is authoritative
          billingAddress: lenId ? undefined : lenRequest?.billingAddress,
          serviceAddresses
        });
      }

      // Multiple LENs resolving to the same existing ID: at customer/portfolio
      // level that means the user matched two distinct legal entities to one
      // record in the Legal Entities step — fail fast so they can fix the match.
      // At LEN-or-deeper entry that step doesn't exist and the wizard itself
      // pinned every extracted-name group to the entry LEN, so merge instead.
      const level = store._context()?.level;
      if (level === 'LEN' || level === 'SERVICE_ADDRESS' || level === 'METER') {
        return {
          customerId: Number(customerId),
          legalEntities: mergeSameIdLegalEntities(legalEntities, level)
        };
      }
      const seenLenIds = new Map<string, string>(); // lenId -> groupId
      for (const len of legalEntities) {
        if (!len.id) continue;
        const existing = seenLenIds.get(len.id);
        if (existing) {
          throw new Error(
            `Two legal entities are linked to the same existing record. ` +
            `Go back to the Legal Entities step and change one of the matches. ` +
            `(groupIds: ${existing}, ${len.groupId})`
          );
        }
        seenLenIds.set(len.id, len.groupId);
      }

      return {
        customerId: Number(customerId),
        legalEntities
      };
    },

    // =========================================================================
    // New Customer Request
    // =========================================================================

    getNewCustomerRequest(): PortfolioCustomerCreationRequest {
      const details = store.newCustomerDetails();
      const contact = store.newCustomerContact();
      const preview = computeNewCustomerPreview(store._billFiles, store.matchRequest());

      return {
        customerName: details.customerName || preview.suggestedName,
        address1: details.address1 || '',
        address2: details.address2 || '',
        city: details.city || '',
        state: details.state || '',
        zip: details.zip || '',
        customerType: CustomerAndServiceAddressType.OTHER,
        tags: [],
        active: true,
        addLen: false,
        primaryContact: {
          firstName: contact.firstName || '',
          lastName: contact.lastName || '',
          email: contact.email || '',
          title: contact.title || '',
          phone: contact.phone || '',
          userId: contact.userId || null
        },
        agentLanguageRequired: false,
        agentLanguageNotes: ''
      };
    },

    initializeSelectionsAllNew(): void {
      const response = store.matchResponse();
      if (!response) return;

      const selections: Record<string, number | string | null> = {};

      for (const len of response.legalEntities) {
        selections[len.groupId] = null;
        for (const sa of len.serviceAddresses) {
          selections[sa.groupId] = null;
          for (const meter of sa.meters) {
            selections[meter.groupId] = null;
          }
        }
      }

      patchState(store, { entitySelections: selections });
      (store as any).saveState();
    },

    // =========================================================================
    // Duplicate Handling
    // =========================================================================

    getOverlapTypeLabel(type: OverlapType): string {
      switch (type) {
        case 'EXACT': return 'Duplicate';
        case 'NEAR_EXACT': return 'Duplicate (dates differ by 1 day)';
        case 'PARTIAL': return 'Overlapping dates';
        case 'CONTAINS': return 'Overlapping — new bill contains existing';
        case 'CONTAINED_BY': return 'Overlapping — existing bill contains new';
        default: return type;
      }
    },

    /** The user declined a pairable suggestion — the bill reverts to a plain blocking overlap. */
    rejectPairSuggestion(billGroupId: string): void {
      patchState(store, { rejectedPairSuggestions: { ...store.rejectedPairSuggestions(), [billGroupId]: true } });
      (store as any).saveState();
    },

    isPairRejected(billGroupId: string): boolean {
      return !!store.rejectedPairSuggestions()[billGroupId];
    },

    getBillDuplicate(billGroupId: string): DuplicateWarning | null {
      // Suppressed for bills on new meters — see computeNewMeterBillGroupIds.
      if (store.newMeterBillGroupIds().has(billGroupId)) return null;
      return store.duplicatesMap()[billGroupId] || null;
    },

    /** Records the user's choice for a duplicate. Replace is refused (returns false)
     *  unless the API marked the duplicate replaceable. */
    setDuplicateResolution(billGroupId: string, existingBillId: number, action: DuplicateAction): boolean {
      if (action === 'REPLACE' && store.duplicatesMap()[billGroupId]?.replaceable !== true) {
        return false;
      }
      patchState(store, {
        duplicateResolutions: {
          ...store.duplicateResolutions(),
          [billGroupId]: { billGroupId, existingBillId, action }
        }
      });
      (store as any).saveState();
      return true;
    },

    /** Meter number and service address labels of the meter a bill sits on, as the user sees them. */
    getBillMeterLabels(billGroupId: string): { meter: string; address: string } | null {
      for (const len of store.matchResponse()?.legalEntities ?? []) {
        for (const sa of len.serviceAddresses) {
          for (const meter of sa.meters) {
            if (!meter.bills.some(b => b.groupId === billGroupId)) continue;
            const meter1 = store.meterEdits()[meter.groupId]?.utilityAccountNumber1?.trim()
              || (store as any).getOriginalName(meter.groupId);
            return { meter: meter1, address: (store as any).getOriginalName(sa.groupId) };
          }
        }
      }
      return null;
    },

    /** Customer, address, meter and commodity of the meter a bill is being checked against,
     *  for fetching an existing bill's stored PDF. Null when the meter isn't an existing one. */
    getExistingBillRef(billGroupId: string): ExistingBillRef | null {
      const resp = store.matchResponse();
      const customerId = store.selectedCustomerId() ?? store._context()?.customerId;
      if (!resp || !customerId) return null;
      for (const len of resp.legalEntities) {
        for (const sa of len.serviceAddresses) {
          for (const meter of sa.meters) {
            if (!meter.bills.some(b => b.groupId === billGroupId)) continue;
            const saId = sa.resolvedId ?? (Number(store.entitySelections()[sa.groupId]) || null);
            const meterId = meter.resolvedId ?? (Number(store.entitySelections()[meter.groupId]) || null);
            if (saId == null || meterId == null) return null;
            const ctx = resolveMeterContext({
              meterGroupId: meter.groupId,
              response: resp,
              request: store.matchRequest(),
              saEdits: store.saEdits(),
            });
            return {
              customerId: Number(customerId),
              serviceAddressId: saId,
              meterId,
              commodityType: ctx?.commodityType ?? CommodityType.Electricity,
            };
          }
        }
      }
      return null;
    },

    /** Opens a bill's review form from the summary (the express overview shows that bill's detail). */
    goToBillReview(billGroupId: string): void {
      const idx = store.billNavigation().findIndex(n => n.billGroupId === billGroupId);
      if (idx < 0) return;
      patchState(store, {
        currentBillIndex: idx,
        currentStep: 'bill-review',
        error: null,
        previewBillFileId: null,
        openBillDetailRequested: store.fastTrackMode() === 'fast',
      });
      (store as any).saveState();
    },

    /** Reads and clears the one-shot request to open the bill detail on entering the express overview. */
    consumeOpenBillDetailRequest(): boolean {
      const requested = store.openBillDetailRequested();
      if (requested) patchState(store, { openBillDetailRequested: false });
      return requested;
    },

    autoResolveDuplicates(): void {
      const resolutions = { ...store.duplicateResolutions() };
      // unresolvedDuplicates is already EXACT/NEAR_EXACT-only, resolution-free,
      // and excludes bills on new meters.
      for (const { billGroupId, duplicate } of store.unresolvedDuplicates()) {
        resolutions[billGroupId] = {
          billGroupId,
          existingBillId: duplicate.existingBillId,
          action: 'SKIP'
        };
      }
      patchState(store, { duplicateResolutions: resolutions });
      (store as any).saveState();
    },

    setDuplicates(duplicates: Record<string, DuplicateWarning>): void {
      patchState(store, { duplicatesMap: duplicates });
      (store as any).saveState();
    },

    clearDuplicateResolutions(): void {
      patchState(store, { duplicateResolutions: {} });
      (store as any).saveState();
    },

    // =========================================================================
    // New Customer Contact & Details
    // =========================================================================

    updateNewCustomerContact(field: keyof NewCustomerContact, value: string): void {
      patchState(store, {
        newCustomerContact: { ...store.newCustomerContact(), [field]: value }
      });
      (store as any).saveState();
    },

    patchNewCustomerContact(patch: Partial<NewCustomerContact>): void {
      patchState(store, {
        newCustomerContact: { ...store.newCustomerContact(), ...patch }
      });
      (store as any).saveState();
    },

    updateNewCustomerDetails(field: keyof NewCustomerDetails, value: string): void {
      patchState(store, {
        newCustomerDetails: { ...store.newCustomerDetails(), [field]: value }
      });
      (store as any).saveState();
    },

    getEditableCustomerDetails(): NewCustomerDetails {
      const details = store.newCustomerDetails();
      const preview = computeNewCustomerPreview(store._billFiles, store.matchRequest());

      if (!details.customerName && preview.suggestedName) {
        return { ...details, customerName: preview.suggestedName };
      }
      return details;
    },

    isNewCustomerContactValid(): boolean {
      const contact = store.newCustomerContact();
      return !!(contact.firstName?.trim() && contact.lastName?.trim() && contact.email?.trim())
        && EMAIL_REGEXP.test(contact.email.trim());
    },

    /**
     * Build a preview of the new customer to be created.
     * Delegates to standalone computeNewCustomerPreview function.
     */
    getNewCustomerPreview(): NewCustomerPreview {
      return computeNewCustomerPreview(store._billFiles, store.matchRequest());
    },

    // =========================================================================
    // Context
    // =========================================================================

    getContext(): OnboardingContext | null {
      return store._context();
    },

    // =========================================================================
    // Parent-Conflict Resolution — Use Existing (local) and Change Existing (PUT)
    // =========================================================================

    /**
     * Use Existing (local rewrite): match the imported SA to the parent's full
     * address. Patches saEdits + matchRequest so display, payload, and the
     * conflict-detector all see consistent values. The conflict naturally drops
     * out on the next change-detection pass.
     */
    applyParentSaToImported(
      saGroupId: string,
      parent: { street1: string; street2: string; city: string; state: string; zip: string }
    ): void {
      const currentEdit = store.saEdits()[saGroupId] ?? {};
      patchState(store, {
        saEdits: { ...store.saEdits(), [saGroupId]: { ...currentEdit, ...parent } },
      });
      const request = store.matchRequest();
      if (request) {
        patchState(store, { matchRequest: patchSaAddressInRequest(request, saGroupId, parent) });
      }
      (store as any).saveState();
    },

    /**
     * Use Existing (local rewrite) for LEN name. Patches both matchRequest and
     * entityNameOverrides so buildImportRequest and the UI breadcrumb stay in
     * sync. Does NOT touch billing address — caller can pair with
     * updateLenBillingAddress if they want a full sync.
     */
    updateLenName(groupId: string, name: string): void {
      const request = store.matchRequest();
      if (request) {
        const updatedLens = request.legalEntities.map(len =>
          len.groupId === groupId ? { ...len, name } : len
        );
        patchState(store, { matchRequest: { ...request, legalEntities: updatedLens } });
      }
      patchState(store, {
        entityNameOverrides: { ...store.entityNameOverrides(), [groupId]: name },
      });
      (store as any).saveState();
    },

    /**
     * Change Existing (destructive): rename the existing portfolio LEN.
     * GETs the full LEN form first so the PUT preserves every other field
     * (billing address, EIN, tags, contacts) — the API does a full replace,
     * so a partial body would wipe unspecified fields. Syncs local store
     * state on success. Throws on failure (caller surfaces the error / Retry).
     */
    async commitParentLenChange(groupId: string, newName: string): Promise<void> {
      const ctx = store._context();
      if (!ctx || !ctx.customerId || !ctx.lenId) {
        throw new Error('commitParentLenChange requires customerId and lenId in context');
      }
      const customerId = Number(ctx.customerId);
      const lenId = String(ctx.lenId);

      const existing: LenV4FormModel = await firstValueFrom(
        (store as any)._lenService.getLenById(customerId, lenId)
      );
      const lenForm: LenV4FormModel = { ...existing, name: newName };
      await firstValueFrom(
        (store as any)._lenService.updateLen(customerId, lenId, lenForm)
      );

      const request = store.matchRequest();
      if (request) {
        const updatedLens = request.legalEntities.map(len =>
          len.groupId === groupId ? { ...len, name: newName } : len
        );
        patchState(store, { matchRequest: { ...request, legalEntities: updatedLens } });
      }
      patchState(store, {
        entityNameOverrides: { ...store.entityNameOverrides(), [groupId]: newName },
      });
      // The existing LEN now matches the bill — advance the parent side of the
      // conflict comparison too, otherwise detectConflicts keeps seeing the stale
      // ctx.lenName and the resolved row never clears (user stuck in a loop).
      store._context.set({ ...ctx, lenName: newName });
      (store as any).saveState();
    },

    /**
     * Change Existing (destructive): rewrite the existing portfolio SA address.
     * GETs the full SA record first so the PUT preserves non-address fields
     * (label, lenId, type, subType, tags) — the API does a full replace, so a
     * partial body would wipe unspecified fields. State is forced to the
     * parent's value by the caller (state edits are blocked at the resolver UX
     * layer). Syncs local store state on success. Throws on failure.
     */
    async commitParentSaChange(
      saGroupId: string,
      address: { street1: string; street2: string; city: string; state: string; zip: string }
    ): Promise<void> {
      const ctx = store._context();
      if (!ctx || !ctx.customerId) {
        throw new Error('commitParentSaChange requires customerId in context');
      }
      const customerId = Number(ctx.customerId);
      const saId = Number(ctx.serviceAddressId);
      if (!saId) throw new Error('commitParentSaChange requires serviceAddressId in context');

      const detail: ServiceAddressDetailsModel = await firstValueFrom(
        (store as any)._saService.getServiceAddressById(customerId, saId)
      );
      const dto: ServiceAddressAddUpdateDTO = {
        id: detail.id,
        customerId: detail.customerId,
        street1: address.street1,
        street2: address.street2,
        city: address.city,
        state: address.state,
        zip: address.zip,
        label: detail.label,
        lenId: detail.lenId,
        type: detail.type,
        subType: detail.subType,
        imageTempId: '',
        tags: detail.tags ?? [],
        activationChange: { active: detail.active, id: detail.activationAudit?.id },
        active: detail.active,
      };
      await firstValueFrom(
        (store as any)._saService.updateServiceAddress(customerId, saId, dto)
      );

      const currentEdit = store.saEdits()[saGroupId] ?? {};
      patchState(store, {
        saEdits: { ...store.saEdits(), [saGroupId]: { ...currentEdit, ...address } },
      });
      const request = store.matchRequest();
      if (request) {
        patchState(store, { matchRequest: patchSaAddressInRequest(request, saGroupId, address) });
      }
      // The existing SA now matches the bill — advance the parent side of the
      // conflict comparison too, otherwise detectConflicts keeps seeing the stale
      // ctx.serviceAddress and the resolved row never clears (user stuck in a loop).
      store._context.set({
        ...ctx,
        serviceAddress: {
          street1: address.street1,
          street2: address.street2,
          city: address.city,
          state: address.state,
          zip: address.zip,
        },
      });
      (store as any).saveState();
    },
  })),

  // No hooks needed — persistence is explicit via saveState() calls
);

// =============================================================================
// Private Helper Functions
// =============================================================================

/**
 * Compute a preview of the new customer to be created.
 * Extracted as standalone function so it can be called from multiple store
 * methods within the same withMethods block (methods in the same block cannot
 * reference each other via the `store` parameter).
 */
function computeNewCustomerPreview(
  billFiles: Map<string, BillFile>,
  matchRequest: BillImportMatchRequest | null
): NewCustomerPreview {
  const processedBills: BillFile[] = [];
  billFiles.forEach(bf => {
    if (bf.data) processedBills.push(bf);
  });

  const legalEntityNames = Array.from(new Set(
    processedBills.map(b => (b.data!.legalEntity || '').trim()).filter(Boolean)
  ));
  const suggestedName = legalEntityNames[0] || 'New Customer';

  const billWithAddress = processedBills.find(b => b.data!.billingAddressStreet1?.trim());
  let billingAddress: string | null = null;
  if (billWithAddress) {
    const d = billWithAddress.data!;
    const parts = [
      d.billingAddressStreet1,
      d.billingAddressStreet2,
      [d.billingAddressCity, d.billingAddressState].filter(Boolean).join(', '),
      d.billingAddressZip
    ].filter(Boolean);
    billingAddress = parts.join(', ');
  }

  let serviceAddressCount = 0;
  let meterCount = 0;
  let billCount = 0;
  if (matchRequest) {
    for (const le of matchRequest.legalEntities) {
      serviceAddressCount += le.serviceAddresses.length;
      for (const sa of le.serviceAddresses) {
        meterCount += sa.meters.length;
        for (const m of sa.meters) {
          billCount += m.bills.length;
        }
      }
    }
  }

  return { suggestedName, billingAddress, legalEntityNames, serviceAddressCount, meterCount, billCount };
}

/**
 * Auto-resolve energy plans for supplier bills during express import.
 * For each supplier bill on an existing meter, calls the plan-finder API.
 * If the nearest plan matches the bill's supplier, auto-applies it.
 *
 * Standalone function (not a store method) because it's called from
 * executeFastTrack() which is in the same withMethods block.
 */
async function autoResolveEnergyPlans(
  response: BillImportMatchResponse,
  matchRequest: BillImportMatchRequest,
  selections: Record<string, number | string | null>,
  billData: Record<string, ParsedBillData>,
  customerId: number,
  contractsService: ContractsV4Service,
  skippedGroupIds: ReadonlySet<string>,
): Promise<{ resolved: number; updatedBillData: Record<string, ParsedBillData> }> {
  // Build entries for all supplier bills that need resolution
  const entries: CustomerPlanFinderEntry[] = [];

  for (const len of response.legalEntities) {
    // Look up the corresponding match request LEN to get state/commodity
    const reqLen = matchRequest.legalEntities.find(l => l.groupId === len.groupId);

    for (const sa of len.serviceAddresses) {
      const reqSa = reqLen?.serviceAddresses.find(s => s.groupId === sa.groupId);
      const saState = reqSa?.state || '';

      for (const meter of sa.meters) {
        const reqMeter = reqSa?.meters.find(m => m.groupId === meter.groupId);
        const commodityType = reqMeter?.commodityType || CommodityType.Electricity;

        const saId = Number(selections[sa.groupId]) || 0;
        const meterId = Number(selections[meter.groupId]) || 0;

        for (const bill of meter.bills) {
          if (bill.supplier?.supplierId == null) continue;
          if (skippedGroupIds.has(bill.groupId)) continue;
          const supplier = bill.supplier;
          const data = billData[bill.groupId];
          if (!data) continue;
          if (data.energyPlanId) continue;
          if (!data.billingPeriodStart || !data.billingPeriodEnd) continue;

          entries.push({
            key: bill.groupId,
            supplierId: supplier.supplierId,
            state: saState,
            commodityType,
            billStartDate: data.billingPeriodStart,
            billEndDate: data.billingPeriodEnd,
            meterId,
            serviceAddressId: saId,
          });
        }
      }
    }
  }

  if (entries.length === 0) return { resolved: 0, updatedBillData: billData };

  try {
    const res = await firstValueFrom(
      contractsService.findEnergyPlansForCustomer(customerId, entries)
    );

    let resolved = 0;
    const updated = { ...billData };

    for (const entry of entries) {
      const result = res.results?.[entry.key];
      if (!result?.nearestPlanId) continue;

      const data = updated[entry.key];
      if (data) {
        updated[entry.key] = { ...data, energyPlanId: result.nearestPlanId };
        resolved++;
      }
    }

    return { resolved, updatedBillData: updated };
  } catch (err) {
    console.warn('Auto-resolve energy plans failed:', err);
    return { resolved: 0, updatedBillData: billData };
  }
}

/**
 * Build bill navigation entries from match response/request.
 * Extracted as standalone function so it can be called from initFromContext
 * (which is in the same withMethods block as buildBillNavigation).
 */
function buildBillNavigationList(
  response: BillImportMatchResponse,
  request: BillImportMatchRequest,
  billFileIdMapping: Record<string, string>,
  billFiles: Map<string, BillFile>,
  billData: Record<string, ParsedBillData>,
  pairs: BillGroup[] = []
): BillNavEntry[] {
  const nav: BillNavEntry[] = [];

  for (const len of response.legalEntities) {
    const lenName = getOriginalNameFromRequest(request, len.groupId);

    for (const sa of len.serviceAddresses) {
      const saAddress = getOriginalNameFromRequest(request, sa.groupId);

      for (const meter of sa.meters) {
        const meterUan = getOriginalNameFromRequest(request, meter.groupId);
        const utilityName = meter.utility?.utilityName || '';

        for (const bill of meter.bills) {
          nav.push({
            lenGroupId: len.groupId,
            lenName,
            saGroupId: sa.groupId,
            saAddress,
            meterGroupId: meter.groupId,
            meterUan,
            utilityName,
            billGroupId: bill.groupId,
            billFileId: billFileIdMapping[bill.groupId] || null,
          });
        }
      }
    }
  }

  // Annotate dual pairs from the authoritative pairing decisions. The
  // supplier bill has no nav entry of its own (it was absorbed into the
  // utility bill's merged record at match time); its PDF hangs off the
  // utility entry for the Supplier tab.
  for (const pair of pairs) {
    if (!pair.utilityBill || !pair.supplierBill) continue;
    if (!billFiles.has(pair.supplierBill.id)) continue;
    const entry = nav.find(e => e.billFileId === pair.utilityBill!.id);
    if (entry) {
      entry.isDualBill = true;
      entry.pairedBillFileId = pair.supplierBill.id;
    }
  }

  return nav;
}

/**
 * Get original name from the match request hierarchy, without needing store reference.
 * Duplicates the logic from getOriginalName but takes a request param.
 */
function getOriginalNameFromRequest(request: BillImportMatchRequest, groupId: string): string {
  for (const len of request.legalEntities) {
    if (len.groupId === groupId) return len.name || 'Unknown';
    for (const sa of len.serviceAddresses) {
      if (sa.groupId === groupId) {
        return [sa.street1, sa.city, sa.state].filter(Boolean).join(', ') || 'Unknown';
      }
      for (const meter of sa.meters) {
        if (meter.groupId === groupId) return meter.utilityAccountNumber1 || 'Unknown';
      }
    }
  }
  return 'Unknown';
}

/**
 * Fallback: rebuilds billFileIdMapping by matching bills to match request entries by billing period.
 * Used when billFileIdMapping was lost/empty in saved state.
 */
function rebuildBillFileIdMapping(
  matchRequest: BillImportMatchRequest,
  bills: BillFile[]
): {
  billFileIdMapping: Record<string, string>;
  billData: Record<string, ParsedBillData>;
  billFilesMap: Record<string, BillFile>;
} {
  const billFileIdMapping: Record<string, string> = {};
  const billData: Record<string, ParsedBillData> = {};
  const billFilesMap: Record<string, BillFile> = {};
  const unmatchedBills = [...bills];

  for (const len of matchRequest.legalEntities) {
    for (const sa of len.serviceAddresses) {
      for (const meter of sa.meters) {
        for (const reqBill of meter.bills) {
          const matchIdx = unmatchedBills.findIndex(b =>
            b.data &&
            b.data.billingPeriodStart === reqBill.billStartDate &&
            b.data.billingPeriodEnd === reqBill.billEndDate
          );

          if (matchIdx >= 0) {
            const matched = unmatchedBills[matchIdx];
            billFileIdMapping[reqBill.groupId] = matched.id;
            if (matched.data) {
              billData[reqBill.groupId] = matched.data;
            }
            billFilesMap[matched.id] = matched;
            unmatchedBills.splice(matchIdx, 1);
          }
        }
      }
    }
  }

  return { billFileIdMapping, billData, billFilesMap };
}

/**
 * Remove bills from a saved wizard session that no longer exist in the current
 * upload set (deleted from the dashboard between sessions), so a restored
 * session doesn't resurrect them in counts or the import request. Meters/SAs/
 * LENs left with no bills are dropped too. Returns null when no bills survive.
 */
function pruneDeletedBillsFromSavedState(
  saved: Partial<BillAiFlowState>,
  currentBillIds: Set<string>
): Partial<BillAiFlowState> | null {
  const mapping = saved.billFileIdMapping ?? {};
  const staleGroupIds = new Set(
    Object.entries(mapping)
      .filter(([, billFileId]) => !currentBillIds.has(billFileId))
      .map(([groupId]) => groupId)
  );
  if (staleGroupIds.size === 0) return saved;

  type PrunableMeter = { bills?: Array<{ groupId: string }> };
  type PrunableSa = { meters?: PrunableMeter[] };

  // The `as T` is the one concession to TS: it can't prove the rebuilt tree
  // still satisfies the generic element type, but the spread preserves every
  // field and only the bills/meters/serviceAddresses arrays are filtered.
  const pruneLens = <T extends { serviceAddresses?: PrunableSa[] }>(lens: T[]): T[] =>
    lens
      .map(len => ({
        ...len,
        serviceAddresses: (len.serviceAddresses ?? [])
          .map(sa => ({
            ...sa,
            meters: (sa.meters ?? [])
              .map(m => ({
                ...m,
                bills: (m.bills ?? []).filter(b => !staleGroupIds.has(b.groupId)),
              }))
              .filter(m => m.bills.length > 0),
          }))
          .filter(sa => sa.meters.length > 0),
      }) as T)
      .filter(len => (len.serviceAddresses ?? []).length > 0);

  const prunedRequest = saved.matchRequest
    ? { ...saved.matchRequest, legalEntities: pruneLens(saved.matchRequest.legalEntities ?? []) }
    : null;
  if (!prunedRequest || prunedRequest.legalEntities.length === 0) return null;

  const prunedResponse = saved.matchResponse
    ? { ...saved.matchResponse, legalEntities: pruneLens(saved.matchResponse.legalEntities ?? []) }
    : saved.matchResponse ?? null;

  const omitStale = <T>(rec: Record<string, T> | undefined): Record<string, T> => {
    const out: Record<string, T> = {};
    for (const [key, value] of Object.entries(rec ?? {})) {
      if (!staleGroupIds.has(key)) out[key] = value;
    }
    return out;
  };

  // Drop merge links where either side of the pair was deleted
  const mergedBills: Record<string, string> = {};
  for (const [supplierId, utilityId] of Object.entries(saved.mergedBills ?? {})) {
    if (!staleGroupIds.has(supplierId) && !staleGroupIds.has(utilityId)) {
      mergedBills[supplierId] = utilityId;
    }
  }

  const billNavigation = (saved.billNavigation ?? []).filter(n => !staleGroupIds.has(n.billGroupId));
  let currentBillIndex = saved.currentBillIndex ?? -1;
  if (currentBillIndex >= billNavigation.length) {
    currentBillIndex = billNavigation.length - 1;
  }

  console.warn(`[BillAiFlowStore] Pruned ${staleGroupIds.size} bill(s) deleted since the session was saved.`);

  return {
    ...saved,
    matchRequest: prunedRequest,
    matchResponse: prunedResponse,
    billData: omitStale(saved.billData),
    billFileIdMapping: omitStale(saved.billFileIdMapping),
    duplicatesMap: omitStale(saved.duplicatesMap),
    duplicateResolutions: omitStale(saved.duplicateResolutions),
    rejectedPairSuggestions: omitStale(saved.rejectedPairSuggestions),
    mergedBills,
    billNavigation,
    currentBillIndex,
  };
}
